// src/ArcPage.tsx — Automatic Raid Creator UI
// Imported by PlannerPage and rendered when mode === "arc"

import React, { useState, useEffect, useCallback } from "react";
import { useToast } from "./Toast";
import { useTimezone } from "./lib/TimezoneContext";
import { DEFAULT_RESET_RULE, ResetRule, getCurrentResetWeek, getResetWeekForDate, localCellToBitIdx, pickerPartsToUtc, resetInstantForWeek, utcToPickerParts } from "./lib/time";
import { DateTime } from "luxon";
import { PickerTzLabel } from "./components/PickerTzLabel";
import {
    BASE_SOLVING_MESSAGES,
    buildSolvingMessages,
    COMMIT_MESSAGES,
} from "./plannerWaitMessages";
import { useRotatingWaitMessage } from "./useRotatingWaitMessage";

// ============================================================================
// TYPES
// ============================================================================
interface ArcPageProps {
    guilds: { id: string; name: string; gameId: string; logoUrl?: string | null }[];
    onCancel: () => void;
    onCommitSuccess: () => void;
}

interface RaidInstance {
    id: number;
    label: string;
    metadata: any;
    image_url?: string | null;
}

interface SpecDefinition {
    class_name: string;
    spec: string;
    role: string; // inferred role for solver matching
}

// Game fields from /games/:gameId/fields — used for class & spec icon resolution.
// Shape mirrors PlannerPage's FieldGroup so the two pages stay interchangeable.
interface FieldOption {
    id: number;
    key: string;
    label: string;
    metadata: any;
    image_url?: string | null;
}
interface FieldGroup {
    id: number;
    key: string;
    label: string;
    multiSelect: boolean;
    isRequired: boolean;
    options: FieldOption[];
}

const WOW_SPECS: Record<string, { spec: string; role: string }[]> = {
    Warrior: [
        { spec: "Protection", role: "Tank" },
        { spec: "Arms", role: "Melee DPS" },
        { spec: "Fury", role: "Melee DPS" },
    ],
    Paladin: [
        { spec: "Holy", role: "Healer" },
        { spec: "Protection", role: "Tank" },
        { spec: "Retribution", role: "Melee DPS" },
    ],
    Hunter: [
        { spec: "Beast Mastery", role: "Ranged DPS" },
        { spec: "Marksmanship", role: "Ranged DPS" },
        { spec: "Survival", role: "Ranged DPS" },
    ],
    Rogue: [
        { spec: "Assassination", role: "Melee DPS" },
        { spec: "Combat", role: "Melee DPS" },
        { spec: "Subtlety", role: "Melee DPS" },
    ],
    Priest: [
        { spec: "Discipline", role: "Healer" },
        { spec: "Holy", role: "Healer" },
        { spec: "Shadow", role: "Ranged DPS" },
    ],
    Shaman: [
        { spec: "Elemental", role: "Ranged DPS" },
        { spec: "Enhancement", role: "Melee DPS" },
        { spec: "Restoration", role: "Healer" },
    ],
    Mage: [
        { spec: "Arcane", role: "Ranged DPS" },
        { spec: "Fire", role: "Ranged DPS" },
        { spec: "Frost", role: "Ranged DPS" },
    ],
    Warlock: [
        { spec: "Affliction", role: "Ranged DPS" },
        { spec: "Demonology", role: "Ranged DPS" },
        { spec: "Destruction", role: "Ranged DPS" },
    ],
    Druid: [
        { spec: "Balance", role: "Ranged DPS" },
        { spec: "Feral", role: "Melee DPS" },
        { spec: "Guardian", role: "Tank" },
        { spec: "Restoration", role: "Healer" },
    ],
};

interface CompositionTemplate {
    id: string;
    name: string;
    raid_size: number;
    tanks_min: number;
    tanks_max: number;
    healers_min: number;
    healers_max: number;
    is_default: boolean;
    metadata?: { spec_bench?: (SpecDefinition | null)[] } | null;
}

interface ArcMember {
    character_id: string;
    character_name: string;
    user_id: string;
    class_name: string;
    primary_spec: string;   // e.g. "Combat", "Fury" — from metadata.primary_spec_label
    role: string;
    available_roles: string[];
    spec_labels?: string[];     // All specs this player has selected, e.g. ["Combat", "Assassination"]
    availability?: string;      // 336-char binary string for client-side time validation
    wcl_perf: number;
    squad_id: string | null;
    squad_name: string | null;
    also_available_raid_indices: number[];
}

interface SquadWarning {
    squad_id: string;
    squad_name: string;
    placed_here: string[];
    placed_elsewhere: { names: string[]; raid_temp_id: string }[];
    not_placed: string[];
}

interface ArcRaid {
    temp_id: string;
    instance_ids: number[];
    instance_name: string;
    day: string;
    date_iso: string;
    start_time: string;
    end_time: string;
    duration_minutes: number;
    size: number;
    target_size: number;
    members: (ArcMember | null)[];   // sparse, length === target_size; null = empty slot
    role_breakdown: { tanks: number; healers: number; dps: number };
    suggested_raid_leader: string | null;
    suggested_loot_master: string | null;
    squad_warnings: SquadWarning[];
    is_manual?: boolean;  // true for raids added via addEmptyRaid(), not from solver
}

interface UnplacedPlayer {
    character_id: string;
    character_name: string;
    user_id: string;
    class_name: string;
    primary_spec: string;   // e.g. "Combat", "Fury" — from metadata.primary_spec_label
    spec_labels?: string[];
    roles: string[];
    availability?: string;  // 336-char binary string for client-side time validation
    wcl_perf: number;
    squad_id: string | null;
    squad_name: string | null;
    reason: string;
    available_raids: string[];
}

interface SquadSplitSummary {
    squad_id: string;
    squad_name: string;
    total_members: number;
    raids_spread_across: string[];
    fully_together: boolean;
    placement_detail: Record<string, string[]>;
    not_placed: string[];
}

interface ArcResults {
    raids: ArcRaid[];
    unplaced: UnplacedPlayer[];
    squad_split_summary: SquadSplitSummary[];
    stats: Record<string, number>;
    error?: string;
}

// ============================================================================
// BILOCATION HELPERS
// ============================================================================
// WoW week day order matches solver DAY_NAMES constant
const ARC_DAY_ORDER = ['Wed', 'Thu', 'Fri', 'Sat', 'Sun', 'Mon', 'Tue'];

function raidTimeOverlap(r1: ArcRaid, r2: ArcRaid): boolean {
    // Keyed on date_iso, not on an index into a fixed Wed-first day list. That
    // list only matched the solver's day order for EU guilds; a US guild's week
    // starts Tuesday, which silently reordered every comparison.
    const toMins = (r: ArcRaid) => {
        const day = r.date_iso
            ? DateTime.fromISO(r.date_iso, { zone: 'utc' }).toMillis() / 86400000
            : ARC_DAY_ORDER.indexOf(r.day);
        const [h, m] = r.start_time.split(':').map(Number);
        return (day >= 0 ? day : 0) * 1440 + h * 60 + m;
    };
    const s1 = toMins(r1), e1 = s1 + (r1.duration_minutes || 120);
    const s2 = toMins(r2), e2 = s2 + (r2.duration_minutes || 120);
    return s1 < e2 && s2 < e1;
}

// Check if a character's availability bitmap covers a raid's full time window.
// Availability is stored as UTC-indexed bits; map local raid cells to UTC bit indices.
function isCharAvailableForRaid(
    availability: string | undefined,
    dateIso: string,
    startTime: string,
    durationMinutes: number,
    userTz: string
): boolean {
    if (!availability || availability.length < 336) return true;
    if (!dateIso) return true;
    const weekStartIso = getResetWeekForDate(new Date(pickerPartsToUtc(dateIso, "12:00", userTz)));
    const anchorLocal = DateTime.fromISO(pickerPartsToUtc(dateIso, "12:00", userTz), { zone: "utc" }).setZone(userTz);
    const dayLocalMon0 = anchorLocal.weekday - 1;
    const [h, m] = startTime.split(':').map(Number);
    const startHalfHourLocal = h * 2 + Math.floor(m / 30);
    const durationSlots = Math.ceil(durationMinutes / 30);
    for (let i = 0; i < durationSlots; i++) {
        const checkSlot = localCellToBitIdx(weekStartIso, dayLocalMon0, startHalfHourLocal + i, userTz);
        if (availability[checkSlot] !== '1') return false;
    }
    return true;
}

// Returns true if userId has a character in any raid other than excludedRaidId
// that overlaps in time with targetRaid (bilocation check).
function isUserBilocated(
    userId: string,
    targetRaid: ArcRaid,
    allRaids: ArcRaid[],
    excludedRaidId?: string
): boolean {
    return allRaids.some(r =>
        r.temp_id !== targetRaid.temp_id &&
        r.temp_id !== excludedRaidId &&
        r.members.some(m => m && m.user_id === userId) &&
        raidTimeOverlap(r, targetRaid)
    );
}

// Converts a dense member array from the solver into a sparse positional array.
// Tanks → Melee fill from the front; Healers → Ranged fill from the back.
// Empty slots between them are null.
function orderMembersToSlots(members: ArcMember[], targetSize: number): (ArcMember | null)[] {
    const tanks  = members.filter(m => m.role === 'Tank');
    const healers = members.filter(m => m.role === 'Healer');
    const melee  = members.filter(m => m.role === 'DPS' && getPreciseRole(m.class_name, m.primary_spec, m.role) === 'Melee DPS');
    const ranged = members.filter(m => m.role === 'DPS' && getPreciseRole(m.class_name, m.primary_spec, m.role) === 'Ranged DPS');
    const slots: (ArcMember | null)[] = Array(targetSize).fill(null);
    // Front: tanks first, then melee
    [...tanks, ...melee].forEach((m, i) => { if (i < targetSize) slots[i] = m; });
    // Back: healers at the very end, ranged just before them
    [...healers, ...ranged].forEach((m, i) => {
        const idx = targetSize - 1 - i;
        if (idx >= 0 && slots[idx] === null) slots[idx] = m;
    });
    return slots;
}

// ============================================================================
// VISUAL UTILS
// ============================================================================
const CLASS_COLORS: Record<string, string> = {
    Warrior: "#C79C6E", Paladin: "#F58CBA", Hunter: "#ABD473", Rogue: "#FFF569",
    Priest: "#FFFFFF", DeathKnight: "#C41F3B", Shaman: "#0070DE", Mage: "#40C7EB",
    Warlock: "#8787ED", Monk: "#00FF96", Druid: "#FF7D0A", DemonHunter: "#A330C9", Evoker: "#33937F",
};

function getClassColor(className: string): string {
    const key = (className || "").replace(/\s+/g, "");
    return CLASS_COLORS[key] || Object.values(CLASS_COLORS).find((_, i) =>
        Object.keys(CLASS_COLORS)[i].toLowerCase() === key.toLowerCase()
    ) || "#A3A3A3";
}

// WCL parse-tier colour. Mirrors the helper in CreateRaidPage / PlannerPage / RosterPage so
// the parse pill on ARC cards reads the same way as everywhere else (artifact / legendary /
// epic / rare / uncommon / common / poor).
function getParseColor(score: number | undefined): string {
    if (score === undefined) return "#555";
    if (score === 100) return "#e5cc80";
    if (score >= 99) return "#e268a8";
    if (score >= 95) return "#ff8000";
    if (score >= 75) return "#a335ee";
    if (score >= 50) return "#0070dd";
    if (score >= 25) return "#1eff00";
    return "#9d9d9d";
}

function getRoleIcon(role: string): string {
    switch (role) {
        case "Tank": return "\u{1F6E1}";
        case "Healer": return "\u{1F49A}";
        case "DPS": return "\u2694\uFE0F";
        default: return "\u{1F464}";
    }
}

// Returns the precise WOW_SPECS role label ("Melee DPS" / "Ranged DPS" / "Tank" / "Healer")
// for a member, using spec to distinguish DPS subtypes.
function getPreciseRole(className: string, primarySpec: string, role: string): string {
    if (role !== "DPS") return role;
    const specEntry = (WOW_SPECS[className] || []).find(s => s.spec === primarySpec);
    return specEntry?.role || role; // "Melee DPS" or "Ranged DPS" or fallback "DPS"
}

// Get role display color using fetched roleColors map (falls back to hardcoded values).
// Pass roleColors from component state.
function getRoleColor(className: string, primarySpec: string, role: string, roleColors: Record<string, string>): string {
    const precise = getPreciseRole(className, primarySpec, role);
    if (roleColors[precise]) return roleColors[precise];
    // Fallbacks matching existing ARC palette
    if (precise === "Tank") return "#38bdf8";
    if (precise === "Healer") return "#34d399";
    if (precise === "Melee DPS") return roleColors["Melee DPS"] || "#fb923c";
    if (precise === "Ranged DPS") return roleColors["Ranged DPS"] || "#a78bfa"; // violet fallback
    return "#fb923c";
}

// Derive the most useful spec name from class + role.
// Unambiguous → spec name. Ambiguous DPS → class name (better than generic "DPS").
function getDisplaySpec(className: string, role: string): string {
    const specs = WOW_SPECS[className] || [];
    const matches = specs.filter(s =>
        s.role === role ||
        (role === "DPS" && (s.role === "Melee DPS" || s.role === "Ranged DPS"))
    );
    if (matches.length === 1) return matches[0].spec;
    // Ambiguous — class name is far more useful to a raid leader than "DPS"
    if (matches.length > 1) return className;
    return role;
}

// Pick the spec that matches the assigned role, preferring the player's own
// selected spec_labels. Used to normalise the solver's reported primary_spec —
// the solver emits the character's default spec (e.g. "Protection" for a Paladin
// with both Prot + Holy selected) regardless of which role it assigned them to.
// When that role is Healer, the displayed spec should be "Holy", not "Protection".
function getRoleConsistentSpec(
    className: string,
    role: string,
    primarySpec: string,
    specLabels: string[] = []
): string {
    const specsForClass = WOW_SPECS[className] || [];
    const roleMatches = (s: { role: string }) =>
        s.role === role ||
        (role === "DPS" && (s.role === "Melee DPS" || s.role === "Ranged DPS"));
    // If primary_spec already matches the assigned role, keep it (respects user preference)
    const primaryEntry = specsForClass.find(s => s.spec === primarySpec);
    if (primaryEntry && roleMatches(primaryEntry)) return primarySpec;
    // Prefer a spec the player has actually selected
    const matchingSelected = (specLabels || []).length > 0
        ? specsForClass.filter(s => roleMatches(s) && specLabels.includes(s.spec))
        : [];
    if (matchingSelected.length > 0) return matchingSelected[0].spec;
    // Fall back to any class spec for the role
    const anyMatching = specsForClass.filter(s => roleMatches(s));
    if (anyMatching.length > 0) return anyMatching[0].spec;
    return primarySpec || getDisplaySpec(className, role);
}

// Resolve a class icon URL from the fetched game_option fields.
// Mirrors PlannerPage.getClassIconUrl so the two pages render identically.
function getClassIconUrl(className: string | null | undefined, fields: FieldGroup[] | null | undefined): string | null {
    if (!className || !fields) return null;
    const classGroup = fields.find(g => g.key === 'class');
    if (!classGroup) return null;
    const opt = classGroup.options.find(o => o.label === className);
    return opt?.image_url || null;
}

// Resolve a spec icon URL. Spec options in the DB carry a metadata.class_key so
// we can disambiguate (e.g. Paladin's "Holy" vs Priest's "Holy").
function getSpecIconUrl(specLabel: string | null | undefined, className: string | null | undefined, fields: FieldGroup[] | null | undefined): string | null {
    if (!specLabel || !fields) return null;
    const specGroup = fields.find(g => g.key === 'spec');
    if (!specGroup) return null;
    const classKey = className?.toLowerCase().replace(/\s+/g, '') || '';
    const opt = specGroup.options.find(o => o.label === specLabel && o.metadata?.class_key === classKey);
    return opt?.image_url || null;
}

function getReasonLabel(reason: string): { label: string; color: string } {
    switch (reason) {
        case "no_availability": return { label: "No Availability Set", color: "text-red-400" };
        case "no_roles": return { label: "No Roles Configured", color: "text-red-400" };
        case "not_confirmed": return { label: "Availability Not Confirmed", color: "text-yellow-400" };
        case "not_interested": return { label: "Not Interested in Instance", color: "text-yellow-400" };
        case "instance_locked": return { label: "Already Locked Out", color: "text-orange-400" };
        case "role_full": return { label: "Role Slots Full", color: "text-blue-400" };
        case "no_viable_slot": return { label: "No Matching Time Slot", color: "text-white/40" };
        default: return { label: reason, color: "text-white/40" };
    }
}

// ============================================================================
// SECTION PANEL HELPER
// ============================================================================
function Section({ label, hint, trailing, children, stepNumber }: {
    label: string;
    hint?: string;
    trailing?: React.ReactNode;
    children: React.ReactNode;
    stepNumber?: number;
}) {
    return (
        <div className="rounded-2xl border border-[#1C2028] bg-[#0D1014] overflow-hidden">
            <div className="px-5 py-3 border-b border-[#1C2028] bg-gradient-to-r from-white/[0.04] to-transparent flex items-center justify-between">
                <div className="flex items-center gap-3">
                    {stepNumber != null && (
                        <span className="w-6 h-6 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-[10px] font-black text-amber-400 shrink-0">
                            {stepNumber}
                        </span>
                    )}
                    <span className="text-xs font-black uppercase tracking-widest text-amber-400/60">{label}</span>
                    {hint && <span className="text-xs text-white/25">{hint}</span>}
                </div>
                {trailing}
            </div>
            <div className="p-5">{children}</div>
        </div>
    );
}

// ============================================================================
// COMPONENT
// ============================================================================
export default function ArcPage({ guilds, onCancel, onCommitSuccess }: ArcPageProps) {
    const userTz = useTimezone();
    const formatMonthDay = useCallback(
        (d: Date) => new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: userTz }).format(d),
        [userTz]
    );
    const formatWeekdayShort = useCallback(
        (d: Date) => new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: userTz }).format(d),
        [userTz]
    );
    const { showToast } = useToast();
    // Step state
    const [step, setStep] = useState<"configure" | "results" | "committing" | "done">("configure");

    // === Configure state ===
    const [selectedGuildId, setSelectedGuildId] = useState(guilds.length === 1 ? guilds[0].id : "");
    // ISO weekday this guild's raid week starts on: 3 = Wednesday (EU),
    // 2 = Tuesday (US). Comes from GET /arc/templates/:guildId.
    const [resetWeekdayIso, setResetWeekdayIso] = useState(3);
    // The guild's full rule, used to pick WHICH week is current. The day grid
    // itself stays on UTC dates (the solver compares against a UTC anchor).
    const [guildResetRule, setGuildResetRule] = useState<ResetRule>(DEFAULT_RESET_RULE);
    const [raidInstances, setRaidInstances] = useState<RaidInstance[]>([]);
    const [selectedInstanceIds, setSelectedInstanceIds] = useState<number[]>([]);
    const [chained, setChained] = useState(false);
    const [weekOffset, setWeekOffset] = useState(0); // 0 = current reset week, 1 = next
    const [useTimeWindow, setUseTimeWindow] = useState(false);
    const [specificDate, setSpecificDate] = useState("");
    const [selectedDays, setSelectedDays] = useState<string[]>([]); // ISO date strings for day-picker
    const [noEarlier, setNoEarlier] = useState("19:00");
    const [noLater, setNoLater] = useState("23:30");
    const [durationOverride, setDurationOverride] = useState<number | null>(null);
    const [templates, setTemplates] = useState<CompositionTemplate[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
    const [showNewTemplate, setShowNewTemplate] = useState(false);
    const [newTemplateName, setNewTemplateName] = useState("");
    const [newTemplateSize, setNewTemplateSize] = useState(10);
    const [newTemplateTanksMin, setNewTemplateTanksMin] = useState(2);
    const [newTemplateTanksMax, setNewTemplateTanksMax] = useState(2);
    const [newTemplateHealersMin, setNewTemplateHealersMin] = useState(2);
    const [newTemplateHealersMax, setNewTemplateHealersMax] = useState(3);
    const [specBench, setSpecBench] = useState<(SpecDefinition | null)[]>([]);
    const [draggedSpec, setDraggedSpec] = useState<{ spec: SpecDefinition; fromSeat: number | null } | null>(null);

    // === Solve state ===
    const [solving, setSolving] = useState(false);
    const [solveError, setSolveError] = useState<string | null>(null);
    const [activeSolvingMessages, setActiveSolvingMessages] = useState<string[]>([...BASE_SOLVING_MESSAGES]);
    const solvingMessage = useRotatingWaitMessage(solving, activeSolvingMessages);
    const [committedStats, setCommittedStats] = useState<{ raids: number; players: number } | null>(null);

    // === Results state ===
    const [results, setResults] = useState<ArcResults | null>(null);
    const [selectedRaids, setSelectedRaids] = useState<Set<string>>(new Set());
    const [commitError, setCommitError] = useState<string | null>(null);
    const [expandedUnplacedGroup, setExpandedUnplacedGroup] = useState<string | null>(null);
    const [hoveredUserId, setHoveredUserId] = useState<string | null>(null);
    const [specModalTarget, setSpecModalTarget] = useState<{ memberId: string; raidTempId: string; member: ArcMember } | null>(null);

    // === Constraint overrides (MILP hard constraints — synced from template or instance defaults) ===
    const [constraintTanksMin, setConstraintTanksMin] = useState<number>(1);
    const [constraintTanksMax, setConstraintTanksMax] = useState<number>(3);
    const [constraintHealersMin, setConstraintHealersMin] = useState<number>(2);
    const [constraintHealersMax, setConstraintHealersMax] = useState<number>(6);
    // Minimum raiders per raid (floor). null = strict (only complete raids).
    // When set below raid size, solver accepts partials down to this floor.
    const [minRaidersFloor, setMinRaidersFloor] = useState<number | null>(null);

    // === Empty raid creation state ===
    const [showAddRaid, setShowAddRaid] = useState(false);
    const [newRaidDate, setNewRaidDate] = useState("");
    const [newRaidTime, setNewRaidTime] = useState("19:00");

    // === Role colors fetched from game options (matches CreateRaidPage) ===
    const [roleColors, setRoleColors] = useState<Record<string, string>>({});

    // === Full game fields cache — needed for class/spec icon lookup (matches PlannerPage pattern) ===
    const [currentGameFields, setCurrentGameFields] = useState<FieldGroup[] | null>(null);

    // === Hovered squad id — enables cross-column highlighting of all squadmates (mirrors hoveredUserId) ===
    const [hoveredSquadId, setHoveredSquadId] = useState<string | null>(null);

    // === Drag-and-drop state ===
    type DragSource = { type: "raid"; raidTempId: string; memberId: string; role: string } | { type: "unplaced"; charId: string; roles: string[] } | null;
    const [dragSource, setDragSource] = useState<DragSource>(null);
    const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dropTarget, setDropTarget] = useState<string | null>(null); // raid temp_id (card background hover, cross-raid fallback)
    const [dropSlotTarget, setDropSlotTarget] = useState<{ raidId: string; slotIndex: number } | null>(null); // exact slot
    const [dropInvalid, setDropInvalid] = useState(false);
    const raidCardRefs = React.useRef<Map<string, HTMLDivElement>>(new Map());
    const allSlotRefs = React.useRef<Map<string, HTMLDivElement>>(new Map()); // key = `${raidTempId}:${slotIndex}` — ALL slots, occupied or empty
    const containerRef = React.useRef<HTMLDivElement>(null);
    const dragMovedRef = React.useRef(false); // true only after real mouse movement, prevents click-to-remove

    // Is overnight?
    const isOvernight = useTimeWindow && noLater && noEarlier && noLater < noEarlier;

    // Fetch raid instances when guild changes
    useEffect(() => {
        if (!selectedGuildId) { setRaidInstances([]); return; }
        const guild = guilds.find(g => g.id === selectedGuildId);
        if (!guild) return;
        fetch(`/games/${encodeURIComponent(guild.gameId)}/fields`, { credentials: "include" })
            .then(r => r.ok ? r.json() : [])
            .then((data: any[]) => {
                setCurrentGameFields(data as FieldGroup[]);
                const raidGroup = data.find((g: any) => g.key === "raid_interest");
                setRaidInstances(raidGroup?.options || []);
                // Extract role colors from the "role" option group
                const roleGroup = data.find((g: any) => g.key === "role");
                if (roleGroup?.options) {
                    const ROLE_KEY_MAP: Record<string, string> = {
                        tank: "Tank", healer: "Healer", melee_dps: "Melee DPS", ranged_dps: "Ranged DPS",
                    };
                    const colors: Record<string, string> = {};
                    for (const opt of roleGroup.options) {
                        const label = ROLE_KEY_MAP[opt.key] || opt.label;
                        const color = opt.metadata?.role_color;
                        if (label && color) colors[label] = color;
                    }
                    setRoleColors(colors);
                }
            })
            .catch(() => setRaidInstances([]));
    }, [selectedGuildId, guilds]);

    // Fetch composition templates when guild changes
    useEffect(() => {
        if (!selectedGuildId) { setTemplates([]); return; }
        fetch(`/arc/templates/${encodeURIComponent(selectedGuildId)}`, { credentials: "include" })
            .then(r => r.ok ? r.json() : { templates: [] })
            .then(data => {
                setTemplates(data.templates || []);
                // The guild's reset day, so the day picker labels the week the
                // same way the solver will (Wed-first EU, Tue-first US).
                if (data.resetWeekdayIso) setResetWeekdayIso(data.resetWeekdayIso);
                setGuildResetRule(data.resetRule || DEFAULT_RESET_RULE);
                const def = (data.templates || []).find((t: CompositionTemplate) => t.is_default);
                if (def) setSelectedTemplateId(def.id);
            })
            .catch(() => setTemplates([]));
    }, [selectedGuildId]);

    // Sync MILP constraints whenever template or instance selection changes
    useEffect(() => {
        const template = templates.find(t => t.id === selectedTemplateId);
        if (template) {
            setConstraintTanksMin(template.tanks_min);
            setConstraintTanksMax(template.tanks_max);
            setConstraintHealersMin(template.healers_min);
            setConstraintHealersMax(template.healers_max);
        } else if (selectedInstanceIds.length > 0) {
            const inst = raidInstances.find(i => selectedInstanceIds.includes(i.id));
            const raidSize = inst?.metadata?.size || 10;
            // Derive sensible defaults from raid size when not set on the instance
            const szTanksMin  = raidSize <= 10 ? 2 : raidSize <= 25 ? 2 : 3;
            const szTanksMax  = raidSize <= 10 ? 3 : raidSize <= 25 ? 5 : 8;
            const szHealMin   = raidSize <= 10 ? 2 : raidSize <= 25 ? 5 : 8;
            const szHealMax   = raidSize <= 10 ? 3 : raidSize <= 25 ? 8 : 14;
            setConstraintTanksMin(inst?.metadata?.tanks_min  ?? szTanksMin);
            setConstraintTanksMax(inst?.metadata?.tanks_max  ?? szTanksMax);
            setConstraintHealersMin(inst?.metadata?.healers_min ?? szHealMin);
            setConstraintHealersMax(inst?.metadata?.healers_max ?? szHealMax);
        }
    }, [selectedTemplateId, selectedInstanceIds, templates, raidInstances]);

    // Toggle instance selection
    const toggleInstance = (id: number) => {
        setSelectedInstanceIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    // Delete template
    const handleDeleteTemplate = async (templateId: string) => {
        if (!selectedGuildId) return;
        try {
            const res = await fetch(`/arc/templates/${encodeURIComponent(selectedGuildId)}/${encodeURIComponent(templateId)}`, {
                method: "DELETE",
                credentials: "include",
            });
            if (res.ok) {
                setTemplates(prev => prev.filter(t => t.id !== templateId));
                if (selectedTemplateId === templateId) setSelectedTemplateId(null);
            }
        } catch (e) { console.error("Failed to delete template:", e); }
    };

    // Create template
    const handleCreateTemplate = async () => {
        if (!newTemplateName || !selectedGuildId) return;
        try {
            const res = await fetch(`/arc/templates/${encodeURIComponent(selectedGuildId)}`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: newTemplateName,
                    raid_size: newTemplateSize,
                    tanks_min: newTemplateTanksMin,
                    tanks_max: newTemplateTanksMax,
                    healers_min: newTemplateHealersMin,
                    healers_max: newTemplateHealersMax,
                    is_default: templates.length === 0,
                    metadata: specBench.some(s => s !== null) ? { spec_bench: specBench } : undefined,
                }),
            });
            if (res.ok) {
                const data = await res.json();
                setTemplates(prev => [...prev, data.template]);
                setSelectedTemplateId(data.template.id);
                setShowNewTemplate(false);
                setNewTemplateName("");
                setSpecBench([]);
                setDraggedSpec(null);
            }
        } catch (e) { console.error("Failed to create template:", e); }
    };

    // Solve
    const handleSolve = async () => {
        if (!selectedGuildId || selectedInstanceIds.length === 0) return;
        // Build personalized messages from previous results if available
        const playerNames = results
            ? [...results.raids.flatMap(r => r.members.filter((m): m is ArcMember => m !== null).map(m => m.character_name)), ...results.unplaced.map(u => u.character_name)]
            : [];
        setActiveSolvingMessages(buildSolvingMessages(playerNames));
        setSolving(true);
        setSolveError(null);

        const body: any = {
            guild_id: selectedGuildId,
            instance_ids: selectedInstanceIds,
        };
        if (chained && selectedInstanceIds.length > 1) {
            body.chained_instance_ids = selectedInstanceIds;
        }
        if (useTimeWindow) {
            body.time_window = {
                noEarlier,
                noLater,
                ...(selectedDays.length === 1 ? { day: selectedDays[0] } : {}),
                ...(selectedDays.length > 1  ? { days: selectedDays } : {}),
            };
        }
        if (selectedTemplateId) {
            body.template_id = selectedTemplateId;
        }
        if (durationOverride) {
            body.duration_minutes = durationOverride;
        }
        body.week_offset = weekOffset;
        // Always send the effective MILP hard constraints (allows inline adjustment)
        body.tanks_min = constraintTanksMin;
        body.tanks_max = constraintTanksMax;
        body.healers_min = constraintHealersMin;
        body.healers_max = constraintHealersMax;
        // Always send the floor. "Strict" (minRaidersFloor === null) means floor = full raid size,
        // NOT "no floor". Without this, the backend falls back to its permissive default
        // (tanks_min + healers_min ≈ 3), which was letting 2/10 and 3/10 partials through.
        const _floorTmpl = templates.find(t => t.id === selectedTemplateId);
        const _floorInstMeta = raidInstances.find(i => selectedInstanceIds.includes(i.id))?.metadata;
        const _floorEffSize: number = _floorTmpl?.raid_size || _floorInstMeta?.size || 10;
        body.min_raiders_per_raid = minRaidersFloor ?? _floorEffSize;

        try {
            const res = await fetch("/arc/solve", {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            const data = await res.json();
            if (!res.ok) {
                setSolveError(data.error || "Solver failed");
                setSolving(false);
                return;
            }
            // Normalise each member's primary_spec so it matches the role the solver
            // actually assigned (solver emits the character's default spec regardless
            // of assignment — see getRoleConsistentSpec). Must happen BEFORE
            // orderMembersToSlots because that helper uses primary_spec to decide
            // Melee vs Ranged DPS positioning.
            const normalisedRaids = (data.raids || []).map((r: any) => ({
                ...r,
                members: (r.members || []).map((m: ArcMember) => ({
                    ...m,
                    primary_spec: getRoleConsistentSpec(m.class_name, m.role, m.primary_spec, m.spec_labels || []),
                })),
            }));
            // Convert each raid's dense members list to a sparse positional array (Tank→Melee front, Healer→Ranged back)
            const transformedData = {
                ...data,
                raids: normalisedRaids.map((r: any) => ({
                    ...r,
                    members: orderMembersToSlots(r.members || [], r.target_size),
                })),
            };
            setResults(transformedData);
            // Auto-select all raids
            setSelectedRaids(new Set((transformedData.raids || []).map((r: ArcRaid) => r.temp_id)));
            setStep("results");
        } catch (e: any) {
            setSolveError(e.message || "Network error");
        } finally {
            setSolving(false);
        }
    };

    // Commit
    const handleCommit = async () => {
        if (!results || selectedRaids.size === 0) return;
        setStep("committing");
        setCommitError(null);

        const raidsToCommit = results.raids
            .filter(r => selectedRaids.has(r.temp_id))
            .map(r => ({
                instance_ids: r.instance_ids,
                name: null,  // auto-generated from instance labels
                day: r.day,  // BUG 3: include day so commit route adds "- Sun" to name
                date_iso: r.date_iso,
                start_time: r.start_time,
                // BUG FIX (timezone): build the UTC timestamp by interpreting
                // (date_iso + start_time) as the user's LOCAL clock — same convention as
                // CreateRaidPage. Without this, arc_routes.js sent a tz-naive string and
                // PG stored it in the session TZ (UTC on Neon), so 21:00 → 23:00 for CEST.
                starts_at: DateTime.fromISO(`${r.date_iso}T${r.start_time}`, { zone: userTz }).toUTC().toISO() || new Date(`${r.date_iso}T${r.start_time}:00`).toISOString(),
                duration_minutes: r.duration_minutes,
                target_size: r.target_size,  // BUG 4/6: capacity, not placed count
                members: r.members.map((m, idx) => m ? {
                    character_id: m.character_id,
                    role: m.role,
                    slot_index: idx,
                } : null).filter(Boolean),
                raid_leader_character_id: r.suggested_raid_leader,
                loot_master_character_id: r.suggested_loot_master,
            }));

        try {
            const res = await fetch("/arc/commit", {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ guild_id: selectedGuildId, raids: raidsToCommit }),
            });
            const data = await res.json();
            if (!res.ok) {
                setCommitError(data.error || "Commit failed");
                setStep("results");
                return;
            }
            const totalPlayers = raidsToCommit.reduce((sum, r) => sum + r.members.length, 0);
            setCommittedStats({ raids: raidsToCommit.length, players: totalPlayers });
            setStep("done");
        } catch (e: any) {
            setCommitError(e.message || "Network error");
            setStep("results");
        }
    };

    // Move player between raids (drag-and-drop simplified as click-to-move)
    const movePlayer = useCallback((memberId: string, fromRaidId: string, toRaidId: string) => {
        if (!results) return;
        setResults(prev => {
            if (!prev) return prev;
            const newRaids = prev.raids.map(r => ({ ...r, members: [...r.members] }));
            const fromRaid = newRaids.find(r => r.temp_id === fromRaidId);
            const toRaid = newRaids.find(r => r.temp_id === toRaidId);
            if (!fromRaid || !toRaid) return prev;

            const memberIdx = fromRaid.members.findIndex(m => m && m.character_id === memberId);
            if (memberIdx === -1) return prev;

            // Capacity guard for cross-raid moves: full when no null slots
            if (fromRaidId !== toRaidId && !toRaid.members.some(m => m === null)) return prev;

            const member = fromRaid.members[memberIdx]!;
            fromRaid.members[memberIdx] = null;

            if (fromRaidId === toRaidId) {
                // Same raid: move to first available null slot
                const firstEmpty = fromRaid.members.findIndex(m => m === null);
                if (firstEmpty >= 0) fromRaid.members[firstEmpty] = member;
            } else {
                const firstEmpty = toRaid.members.findIndex(m => m === null);
                if (firstEmpty >= 0) toRaid.members[firstEmpty] = member;
            }

            const rb = (arr: (ArcMember | null)[]) => ({
                tanks: arr.filter(m => m?.role === "Tank").length,
                healers: arr.filter(m => m?.role === "Healer").length,
                dps: arr.filter(m => m?.role === "DPS").length,
            });
            for (const raid of [fromRaid, toRaid]) {
                raid.role_breakdown = rb(raid.members);
                raid.size = raid.members.filter(Boolean).length;
            }

            return { ...prev, raids: newRaids };
        });
    }, [results]);

    // Swap two players (same or different raids)
    const swapPlayers = useCallback((memberAId: string, raidAId: string, memberBId: string, raidBId: string) => {
        if (!results) return;
        setResults(prev => {
            if (!prev) return prev;
            const newRaids = prev.raids.map(r => ({ ...r, members: [...r.members] }));
            const raidA = newRaids.find(r => r.temp_id === raidAId);
            const raidB = newRaids.find(r => r.temp_id === raidBId);
            if (!raidA || !raidB) return prev;
            const idxA = raidA.members.findIndex(m => m && m.character_id === memberAId);
            const idxB = raidB.members.findIndex(m => m && m.character_id === memberBId);
            if (idxA === -1 || idxB === -1) return prev;
            if (raidAId === raidBId) {
                // Same raid: swap positions in-place
                [raidA.members[idxA], raidA.members[idxB]] = [raidA.members[idxB], raidA.members[idxA]];
            } else {
                // Different raids: cross-swap
                const memberA = raidA.members[idxA];
                const memberB = raidB.members[idxB];
                raidA.members[idxA] = memberB;
                raidB.members[idxB] = memberA;
            }
            const raidsToUpdate = raidAId === raidBId ? [raidA] : [raidA, raidB];
            for (const raid of raidsToUpdate) {
                raid.role_breakdown = {
                    tanks: raid.members.filter(m => m?.role === "Tank").length,
                    healers: raid.members.filter(m => m?.role === "Healer").length,
                    dps: raid.members.filter(m => m?.role === "DPS").length,
                };
                raid.size = raid.members.filter(Boolean).length;
            }
            return { ...prev, raids: newRaids };
        });
    }, [results]);

    // Swap an unplaced player into a specific seat, displacing that member back to unplaced
    const swapUnplacedWithMember = useCallback((unplacedCharId: string, targetMemberId: string, targetRaidId: string, role: string) => {
        if (!results) return;
        setResults(prev => {
            if (!prev) return prev;
            const unplaced = prev.unplaced.find(u => u.character_id === unplacedCharId);
            if (!unplaced) return prev;
            const newRaids = prev.raids.map(r => ({ ...r, members: [...r.members] }));
            const targetRaid = newRaids.find(r => r.temp_id === targetRaidId);
            if (!targetRaid) return prev;
            const memberIdx = targetRaid.members.findIndex(m => m && m.character_id === targetMemberId);
            if (memberIdx === -1) return prev;
            const displaced = targetRaid.members[memberIdx]!;
            targetRaid.members[memberIdx] = {
                character_id: unplaced.character_id,
                character_name: unplaced.character_name,
                user_id: unplaced.user_id,
                class_name: unplaced.class_name,
                primary_spec: unplaced.primary_spec,
                spec_labels: unplaced.spec_labels,
                role,
                available_roles: unplaced.roles || [role],
                availability: unplaced.availability,
                wcl_perf: unplaced.wcl_perf,
                squad_id: unplaced.squad_id,
                squad_name: unplaced.squad_name,
                also_available_raid_indices: [],
            };
            targetRaid.role_breakdown = {
                tanks: targetRaid.members.filter(m => m?.role === "Tank").length,
                healers: targetRaid.members.filter(m => m?.role === "Healer").length,
                dps: targetRaid.members.filter(m => m?.role === "DPS").length,
            };
            targetRaid.size = targetRaid.members.filter(Boolean).length;
            const newUnplacedList = prev.unplaced.filter(u => u.character_id !== unplacedCharId);
            newUnplacedList.push({
                character_id: displaced.character_id,
                character_name: displaced.character_name,
                user_id: displaced.user_id,
                class_name: displaced.class_name,
                primary_spec: displaced.primary_spec,
                spec_labels: displaced.spec_labels,
                roles: displaced.available_roles || [displaced.role],
                availability: displaced.availability,
                wcl_perf: displaced.wcl_perf,
                squad_id: displaced.squad_id,
                squad_name: displaced.squad_name,
                reason: "role_full",
                available_raids: newRaids
                    .filter(r => !isUserBilocated(displaced.user_id, r, newRaids) &&
                        isCharAvailableForRaid(displaced.availability, r.date_iso, r.start_time, r.duration_minutes, userTz))
                    .map(r => r.temp_id),
            });
            return { ...prev, raids: newRaids, unplaced: newUnplacedList };
        });
    }, [results, userTz]);

    // Insert unplaced player at a specific slot position
    const insertUnplacedAtSlot = useCallback((charId: string, raidTempId: string, slotIndex: number, role: string) => {
        if (!results) return;
        setResults(prev => {
            if (!prev) return prev;
            const unplaced = prev.unplaced.find(u => u.character_id === charId);
            if (!unplaced) return prev;
            const targetRaid = prev.raids.find(r => r.temp_id === raidTempId);
            if (!targetRaid) return prev;
            if (slotIndex < 0 || slotIndex >= targetRaid.target_size) return prev;
            if (targetRaid.members[slotIndex] !== null) return prev; // slot occupied
            if (isUserBilocated(unplaced.user_id, targetRaid, prev.raids)) return prev;
            if (!isCharAvailableForRaid(unplaced.availability, targetRaid.date_iso, targetRaid.start_time, targetRaid.duration_minutes, userTz)) return prev;
            const newMember: ArcMember = {
                character_id: unplaced.character_id,
                character_name: unplaced.character_name,
                user_id: unplaced.user_id,
                class_name: unplaced.class_name,
                primary_spec: unplaced.primary_spec,
                spec_labels: unplaced.spec_labels,
                role,
                available_roles: unplaced.roles || [role],
                availability: unplaced.availability,
                wcl_perf: unplaced.wcl_perf,
                squad_id: unplaced.squad_id,
                squad_name: unplaced.squad_name,
                also_available_raid_indices: [],
            };
            const newRaids = prev.raids.map(r => {
                if (r.temp_id !== raidTempId) return r;
                const arr = [...r.members];
                arr[slotIndex] = newMember;  // direct slot assignment — no shifting
                return {
                    ...r, members: arr, size: arr.filter(Boolean).length,
                    role_breakdown: {
                        tanks: arr.filter(m => m?.role === "Tank").length,
                        healers: arr.filter(m => m?.role === "Healer").length,
                        dps: arr.filter(m => m?.role === "DPS").length,
                    },
                };
            });
            return { ...prev, raids: newRaids, unplaced: prev.unplaced.filter(u => u.character_id !== charId) };
        });
    }, [results, userTz]);

    // Move a raid member to a specific slot index in the same or another raid
    const movePlayerToSlot = useCallback((memberId: string, fromRaidId: string, toRaidId: string, slotIndex: number) => {
        if (!results) return;
        setResults(prev => {
            if (!prev) return prev;
            const fromRaid = prev.raids.find(r => r.temp_id === fromRaidId);
            const toRaid = prev.raids.find(r => r.temp_id === toRaidId);
            if (!fromRaid || !toRaid) return prev;
            const fromIdx = fromRaid.members.findIndex(m => m && m.character_id === memberId);
            if (fromIdx === -1) return prev;
            const member = fromRaid.members[fromIdx]!;
            // Target slot must be empty (or it's the same slot in same raid — no-op)
            if (fromRaidId === toRaidId && fromIdx === slotIndex) return prev;
            if (toRaid.members[slotIndex] !== null) return prev;
            // Cross-raid: bilocation + availability
            if (fromRaidId !== toRaidId && isUserBilocated(member.user_id, toRaid, prev.raids, fromRaidId)) return prev;
            const newRaids = prev.raids.map(r => {
                if (r.temp_id === fromRaidId) {
                    const arr = [...r.members];
                    arr[fromIdx] = null;
                    if (fromRaidId === toRaidId) arr[slotIndex] = member;
                    return { ...r, members: arr, size: arr.filter(Boolean).length,
                        role_breakdown: { tanks: arr.filter(m => m?.role === "Tank").length, healers: arr.filter(m => m?.role === "Healer").length, dps: arr.filter(m => m?.role === "DPS").length } };
                }
                if (r.temp_id === toRaidId && fromRaidId !== toRaidId) {
                    const arr = [...r.members];
                    arr[slotIndex] = member;
                    return { ...r, members: arr, size: arr.filter(Boolean).length,
                        role_breakdown: { tanks: arr.filter(m => m?.role === "Tank").length, healers: arr.filter(m => m?.role === "Healer").length, dps: arr.filter(m => m?.role === "DPS").length } };
                }
                return r;
            });
            return { ...prev, raids: newRaids };
        });
    }, [results]);

    // Remove player from raid (move back to unplaced) — declared before drag effect (effect calls this).
    const removePlayerFromRaid = useCallback((memberId: string, raidTempId: string) => {
        if (!results) return;
        setResults(prev => {
            if (!prev) return prev;
            const newRaids = prev.raids.map(r => ({ ...r, members: [...r.members] }));
            const raid = newRaids.find(r => r.temp_id === raidTempId);
            if (!raid) return prev;
            const memberIdx = raid.members.findIndex(m => m && m.character_id === memberId);
            if (memberIdx === -1) return prev;
            const member = raid.members[memberIdx]!;
            raid.members[memberIdx] = null;  // leave slot empty, preserve positions
            raid.role_breakdown = {
                tanks: raid.members.filter(m => m?.role === "Tank").length,
                healers: raid.members.filter(m => m?.role === "Healer").length,
                dps: raid.members.filter(m => m?.role === "DPS").length,
            };
            raid.size = raid.members.filter(Boolean).length;
            // Add removed member to unplaced — filter to raids they're actually available for
            const removedEntry: UnplacedPlayer = {
                character_id: member.character_id,
                character_name: member.character_name,
                user_id: member.user_id,
                class_name: member.class_name,
                primary_spec: member.primary_spec,
                roles: member.available_roles || [member.role],
                availability: member.availability,
                wcl_perf: member.wcl_perf,
                squad_id: member.squad_id,
                squad_name: member.squad_name,
                reason: "role_full",
                available_raids: newRaids
                    .filter(r => !isUserBilocated(member.user_id, r, newRaids) &&
                        isCharAvailableForRaid(member.availability, r.date_iso, r.start_time, r.duration_minutes, userTz))
                    .map(r => r.temp_id),
            };
            // When a player is removed, other unplaced players of the same user may now be freed.
            // Re-check available_raids for same-user unplaced players based on current raid state.
            const updatedUnplaced = prev.unplaced.map(u => {
                if (u.user_id !== member.user_id) return u;
                const nowAvailable = newRaids
                    .filter(r => !isUserBilocated(u.user_id, r, newRaids) &&
                        isCharAvailableForRaid(u.availability, r.date_iso, r.start_time, r.duration_minutes, userTz))
                    .map(r => r.temp_id);
                const merged = Array.from(new Set([...u.available_raids, ...nowAvailable]));
                return { ...u, available_raids: merged };
            });
            return { ...prev, raids: newRaids, unplaced: [...updatedUnplaced, removedEntry] };
        });
    }, [results, userTz]);

    // Add unplaced player to a raid — declared before drag effect (effect calls this).
    const addUnplacedToRaid = useCallback((charId: string, raidTempId: string, role: string) => {
        if (!results) return;
        setResults(prev => {
            if (!prev) return prev;
            const unplaced = prev.unplaced.find(u => u.character_id === charId);
            if (!unplaced) return prev;

            // BUG 1: bail early if raid doesn't exist — never orphan a player
            const targetRaid = prev.raids.find(r => r.temp_id === raidTempId);
            if (!targetRaid) return prev;

            // BUG 2: respect capacity — no empty slots means full
            if (!targetRaid.members.some(m => m === null)) return prev;

            // BUG 6: bilocation check
            if (isUserBilocated(unplaced.user_id, targetRaid, prev.raids)) return prev;

            // Availability check: player must be free at this raid's time
            if (!isCharAvailableForRaid(unplaced.availability, targetRaid.date_iso, targetRaid.start_time, targetRaid.duration_minutes, userTz)) return prev;

            const newRaids = prev.raids.map(r => {
                if (r.temp_id !== raidTempId) return r;
                const emptyIdx = r.members.findIndex(m => m === null);
                if (emptyIdx === -1) return r;
                const newMembers = [...r.members];
                newMembers[emptyIdx] = {
                    character_id: unplaced.character_id,
                    character_name: unplaced.character_name,
                    user_id: unplaced.user_id,
                    class_name: unplaced.class_name,
                    primary_spec: unplaced.primary_spec,
                    spec_labels: unplaced.spec_labels,
                    role,
                    available_roles: unplaced.roles || [role],
                    availability: unplaced.availability,
                    wcl_perf: unplaced.wcl_perf,
                    squad_id: unplaced.squad_id,
                    squad_name: unplaced.squad_name,
                    also_available_raid_indices: [],
                };
                return {
                    ...r,
                    members: newMembers,
                    size: newMembers.filter(Boolean).length,
                    role_breakdown: {
                        tanks: newMembers.filter(m => m?.role === "Tank").length,
                        healers: newMembers.filter(m => m?.role === "Healer").length,
                        dps: newMembers.filter(m => m?.role === "DPS").length,
                    },
                };
            });
            const newUnplaced = prev.unplaced.filter(u => u.character_id !== charId);
            return { ...prev, raids: newRaids, unplaced: newUnplaced };
        });
    }, [results, userTz]);

    // ============================================================================
    // DRAG-AND-DROP EFFECT (Item #5)
    // ============================================================================
    React.useEffect(() => {
        if (!dragSource) return;
        function handleMouseMove(e: MouseEvent) {
            dragMovedRef.current = true;
            setIsDragging(true);
            setDragPos({ x: e.clientX, y: e.clientY });

            let foundSlot: { raidId: string; slotIndex: number } | null = null;
            let foundTarget: string | null = null;
            let foundInvalid = false;

            // === Single pass over ALL slot refs (occupied + empty) — matches CreateRaidPage ===
            allSlotRefs.current.forEach((el, key) => {
                if (!el) return;
                const colonIdx = key.indexOf(":");
                const raidId = key.slice(0, colonIdx);
                const slotIndex = parseInt(key.slice(colonIdx + 1), 10);
                const rect = el.getBoundingClientRect();
                if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) return;
                foundSlot = { raidId, slotIndex };
                foundTarget = raidId;
            });

            if (foundSlot) {
                const { raidId, slotIndex } = foundSlot as { raidId: string; slotIndex: number };
                const targetRaid = results?.raids.find(r => r.temp_id === raidId);
                const occupant = targetRaid?.members[slotIndex] ?? null;

                if (dragSource?.type === "raid") {
                    // Hovering own slot — always valid, nothing to do
                    if (raidId === dragSource.raidTempId && occupant?.character_id === dragSource.memberId) {
                        // no-op
                    } else if (raidId !== dragSource.raidTempId) {
                        // Cross-raid: check dragged member's availability + bilocation
                        const draggedMember = results?.raids.find(r => r.temp_id === dragSource.raidTempId)?.members.find(m => m && m.character_id === dragSource.memberId);
                        const sourceRaid = results?.raids.find(r => r.temp_id === dragSource.raidTempId);
                        if (draggedMember && targetRaid && !isCharAvailableForRaid(draggedMember.availability, targetRaid.date_iso, targetRaid.start_time, targetRaid.duration_minutes, userTz)) foundInvalid = true;
                        if (draggedMember && targetRaid && isUserBilocated(draggedMember.user_id, targetRaid, results?.raids ?? [], dragSource.raidTempId)) foundInvalid = true;
                        // If swapping with occupant, also check occupant's availability at source
                        if (occupant && sourceRaid) {
                            if (!isCharAvailableForRaid(occupant.availability, sourceRaid.date_iso, sourceRaid.start_time, sourceRaid.duration_minutes, userTz)) foundInvalid = true;
                            if (isUserBilocated(occupant.user_id, sourceRaid, results?.raids ?? [], raidId)) foundInvalid = true;
                        }
                    }
                } else if (dragSource?.type === "unplaced") {
                    const unplaced = results?.unplaced.find(u => u.character_id === dragSource.charId);
                    if (unplaced && targetRaid) {
                        if (!isCharAvailableForRaid(unplaced.availability, targetRaid.date_iso, targetRaid.start_time, targetRaid.duration_minutes, userTz)) foundInvalid = true;
                        if (isUserBilocated(unplaced.user_id, targetRaid, results?.raids ?? [])) foundInvalid = true;
                        // Capacity: only an issue if the slot is null AND the raid has no null slots (impossible since the slot IS null) — covered by insertUnplacedAtSlot
                    }
                }
            } else {
                // No specific slot hit — check for cross-raid card background (convenience fallback)
                raidCardRefs.current.forEach((el, raidTempId) => {
                    if (!el) return;
                    const rect = el.getBoundingClientRect();
                    if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) return;
                    foundTarget = raidTempId;
                    // Same raid background — invalid (no free-float within same raid)
                    if (dragSource?.type === "raid" && dragSource.raidTempId === raidTempId) { foundInvalid = true; return; }
                    // Cross-raid card background checks
                    if (dragSource?.type === "raid") {
                        const draggedMember = results?.raids.find(r => r.temp_id === dragSource.raidTempId)?.members.find(m => m && m.character_id === dragSource.memberId);
                        const targetRaid = results?.raids.find(r => r.temp_id === raidTempId);
                        if (draggedMember && targetRaid && !isCharAvailableForRaid(draggedMember.availability, targetRaid.date_iso, targetRaid.start_time, targetRaid.duration_minutes, userTz)) foundInvalid = true;
                        if (draggedMember && targetRaid && isUserBilocated(draggedMember.user_id, targetRaid, results?.raids ?? [], dragSource.raidTempId)) foundInvalid = true;
                        if (targetRaid && !targetRaid.members.some(m => m === null)) foundInvalid = true;
                    } else if (dragSource?.type === "unplaced") {
                        const unplaced = results?.unplaced.find(u => u.character_id === dragSource.charId);
                        const targetRaid = results?.raids.find(r => r.temp_id === raidTempId);
                        if (unplaced && targetRaid && !isCharAvailableForRaid(unplaced.availability, targetRaid.date_iso, targetRaid.start_time, targetRaid.duration_minutes, userTz)) foundInvalid = true;
                        if (unplaced && targetRaid && isUserBilocated(unplaced.user_id, targetRaid, results?.raids ?? [])) foundInvalid = true;
                        if (targetRaid && !targetRaid.members.some(m => m === null)) foundInvalid = true;
                    }
                });
            }

            setDropSlotTarget(foundSlot);
            setDropTarget(foundTarget);
            setDropInvalid(foundInvalid);
        }

        function handleMouseUp(e: MouseEvent) {
            if (dropSlotTarget && !dropInvalid && results) {
                // Dropped on a specific slot — swap if occupied, place if empty
                const { raidId, slotIndex } = dropSlotTarget;
                const targetRaid = results.raids.find(r => r.temp_id === raidId);
                const occupant = targetRaid?.members[slotIndex] ?? null;
                if (dragSource?.type === "raid") {
                    if (occupant && occupant.character_id !== dragSource.memberId) {
                        swapPlayers(dragSource.memberId, dragSource.raidTempId, occupant.character_id, raidId);
                    } else if (!occupant) {
                        movePlayerToSlot(dragSource.memberId, dragSource.raidTempId, raidId, slotIndex);
                    }
                } else if (dragSource?.type === "unplaced") {
                    if (occupant) {
                        swapUnplacedWithMember(dragSource.charId, occupant.character_id, raidId, dragSource.roles[0] || "DPS");
                    } else {
                        insertUnplacedAtSlot(dragSource.charId, raidId, slotIndex, dragSource.roles[0] || "DPS");
                    }
                }
            } else if (dropTarget && !dropSlotTarget && !dropInvalid && results) {
                // Cross-raid card background — first available slot (convenience)
                if (dragSource?.type === "raid") {
                    movePlayer(dragSource.memberId, dragSource.raidTempId, dropTarget);
                } else if (dragSource?.type === "unplaced") {
                    addUnplacedToRaid(dragSource.charId, dropTarget, dragSource.roles[0] || "DPS");
                }
            } else if (!dropSlotTarget && !dropTarget && dragSource?.type === "raid" && dragMovedRef.current) {
                // Dropped outside all raids — move to unplaced
                removePlayerFromRaid(dragSource.memberId, dragSource.raidTempId);
            }
            dragMovedRef.current = false;
            setDragSource(null);
            setDragPos(null);
            setIsDragging(false);
            setDropTarget(null);
            setDropSlotTarget(null);
            setDropInvalid(false);
        }
        
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [
        dragSource,
        dropTarget,
        dropSlotTarget,
        dropInvalid,
        results,
        swapPlayers,
        swapUnplacedWithMember,
        insertUnplacedAtSlot,
        movePlayerToSlot,
        movePlayer,
        addUnplacedToRaid,
        removePlayerFromRaid,
        userTz,
    ]);

    // Change a player's role within their raid
    const changePlayerRole = useCallback((memberId: string, raidTempId: string, newRole: string) => {
        if (!results) return;
        setResults(prev => {
            if (!prev) return prev;
            const newRaids = prev.raids.map(r => {
                if (r.temp_id !== raidTempId) return r;
                const newMembers = r.members.map(m => {
                    if (!m || m.character_id !== memberId) return m;
                    // BUG 3: derive primary_spec for the new role so display text updates
                    const specsForClass = WOW_SPECS[m.class_name] || [];
                    const currentSpecMatchesNewRole = specsForClass.find(
                        s => s.spec === m.primary_spec && s.role === newRole
                    );
                    const firstMatchForRole = specsForClass.find(s => s.role === newRole);
                    const newPrimarySpec = currentSpecMatchesNewRole
                        ? m.primary_spec
                        : (firstMatchForRole?.spec || m.primary_spec);
                    return { ...m, role: newRole, primary_spec: newPrimarySpec };
                });
                return {
                    ...r,
                    members: newMembers,
                    role_breakdown: {
                        tanks: newMembers.filter(m => m?.role === "Tank").length,
                        healers: newMembers.filter(m => m?.role === "Healer").length,
                        dps: newMembers.filter(m => m?.role === "DPS").length,
                    },
                };
            });
            return { ...prev, raids: newRaids };
        });
    }, [results]);

    // Set a specific spec+role for a member (from the spec picker modal)
    // WOW_SPECS uses "Melee DPS" / "Ranged DPS" — normalize to "DPS" for raid storage
    const changePlayerSpec = useCallback((memberId: string, raidTempId: string, newSpec: string, specRole: string) => {
        if (!results) return;
        const normalizedRole = specRole === "Tank" ? "Tank" : specRole === "Healer" ? "Healer" : "DPS";
        setResults(prev => {
            if (!prev) return prev;
            const newRaids = prev.raids.map(r => {
                if (r.temp_id !== raidTempId) return r;
                const newMembers = r.members.map(m => {
                    if (!m || m.character_id !== memberId) return m;
                    return { ...m, primary_spec: newSpec, role: normalizedRole };
                });
                return {
                    ...r,
                    members: newMembers,
                    role_breakdown: {
                        tanks: newMembers.filter(m => m?.role === "Tank").length,
                        healers: newMembers.filter(m => m?.role === "Healer").length,
                        dps: newMembers.filter(m => m?.role === "DPS").length,
                    },
                };
            });
            return { ...prev, raids: newRaids };
        });
    }, [results]);

    // Like addUnplacedToRaid but inserts at a specific slot index instead of appending
    // Add a blank empty raid to the results for manual building / PUG slots
    const addEmptyRaid = useCallback(() => {
        if (!results) return;
        const firstRaid = results.raids[0];
        const dateInput = newRaidDate || firstRaid?.date_iso || DateTime.now().setZone(userTz).toFormat("yyyy-MM-dd");
        const timeInput = newRaidTime || firstRaid?.start_time || "19:00";
        const startUtcIso = pickerPartsToUtc(dateInput, timeInput, userTz);
        const startJs = DateTime.fromISO(startUtcIso, { zone: "utc" }).toJSDate();
        const dayName = formatWeekdayShort(startJs);
        const startParts = utcToPickerParts(startUtcIso, userTz);
        const inst = raidInstances.find(i => selectedInstanceIds.includes(i.id));
        const duration = durationOverride || inst?.metadata?.duration || 120;
        const endUtcIso = DateTime.fromISO(startUtcIso, { zone: "utc" }).plus({ minutes: duration }).toISO() || startUtcIso;
        const endParts = utcToPickerParts(endUtcIso, userTz);
        const targetSize = inst?.metadata?.size || firstRaid?.target_size || 25;
        const newRaid: ArcRaid = {
            temp_id: `manual_${Date.now()}`,
            instance_ids: selectedInstanceIds.length > 0 ? selectedInstanceIds : (firstRaid?.instance_ids || []),
            instance_name: inst?.label || firstRaid?.instance_name || "Custom Raid",
            day: dayName,
            date_iso: startParts.date,
            start_time: startParts.time,
            end_time: endParts.time,
            duration_minutes: duration,
            size: 0,
            target_size: targetSize,
            members: Array(targetSize).fill(null) as (ArcMember | null)[],
            role_breakdown: { tanks: 0, healers: 0, dps: 0 },
            suggested_raid_leader: null,
            suggested_loot_master: null,
            squad_warnings: [],
            is_manual: true,
        };
        setResults(prev => {
            if (!prev) return prev;
            const allRaids = [...prev.raids, newRaid];
            // Make unplaced players available for the new raid only if they're available at that time
            const updatedUnplaced = prev.unplaced.map(u => {
                if (isUserBilocated(u.user_id, newRaid, allRaids)) return u;
                if (!isCharAvailableForRaid(u.availability, newRaid.date_iso, newRaid.start_time, newRaid.duration_minutes, userTz)) return u;
                return { ...u, available_raids: Array.from(new Set([...u.available_raids, newRaid.temp_id])) };
            });
            return { ...prev, raids: allRaids, unplaced: updatedUnplaced };
        });
        setSelectedRaids(prev => { const n = new Set(prev); n.add(newRaid.temp_id); return n; });
        setShowAddRaid(false);
        setNewRaidDate("");
        setNewRaidTime("19:00");
    }, [results, newRaidDate, newRaidTime, raidInstances, selectedInstanceIds, durationOverride, userTz, formatWeekdayShort]);

    // Delete a raid from results
    const deleteRaid = useCallback((raidTempId: string) => {
        if (!results) return;
        // Move members back to unplaced
        setResults(prev => {
            if (!prev) return prev;
            const raid = prev.raids.find(r => r.temp_id === raidTempId);
            const remainingRaids = prev.raids.filter(r => r.temp_id !== raidTempId);
            const displaced = (raid?.members || []).filter((m): m is ArcMember => m !== null).map(m => ({
                character_id: m.character_id,
                character_name: m.character_name,
                user_id: m.user_id,
                class_name: m.class_name,
                primary_spec: m.primary_spec,
                spec_labels: m.spec_labels,
                roles: m.available_roles || [m.role],
                availability: m.availability,
                wcl_perf: m.wcl_perf,
                squad_id: m.squad_id,
                squad_name: m.squad_name,
                reason: "role_full" as string,
                available_raids: remainingRaids
                    .filter(r => !isUserBilocated(m.user_id, r, remainingRaids) &&
                        isCharAvailableForRaid(m.availability, r.date_iso, r.start_time, r.duration_minutes, userTz))
                    .map(r => r.temp_id),
            }));
            return {
                ...prev,
                raids: remainingRaids,
                unplaced: [...prev.unplaced, ...displaced],
            };
        });
        setSelectedRaids(prev => { const n = new Set(prev); n.delete(raidTempId); return n; });
    }, [results, userTz]);

    // ============================================================================
    // RENDER: CONFIGURE STEP
    // ============================================================================
    if (step === "configure") {
        // Compute the 7 days of the selected WoW week for day-picker
        // The 7 days of the selected raid week, starting on the GUILD's reset
        // day. Two things were wrong here before: the week always started
        // Wednesday (EU-only), and it was computed from browser-LOCAL time
        // (now.getDay(), setHours) while the solver compares these ISO dates
        // against a UTC anchor — so a user west of UTC could offer the solver a
        // day it would reject.
        const wowWeekDays = (() => {
            const anchorUtc = resetInstantForWeek(
                getCurrentResetWeek(guildResetRule),
                { weekday: resetWeekdayIso, hour: 0, tz: 'UTC' }
            ).plus({ weeks: weekOffset }).startOf('day');
            return Array.from({ length: 7 }, (_, i) => {
                const d = anchorUtc.plus({ days: i });
                return {
                    name: d.toFormat('ccc'),
                    iso: d.toFormat('yyyy-MM-dd'),
                    short: formatMonthDay(d.toJSDate()),
                };
            });
        })();

        const toggleDay = (iso: string) => {
            setSelectedDays(prev =>
                prev.includes(iso) ? prev.filter(d => d !== iso) : [...prev, iso]
            );
        };

        return (<>
            {/* FUN 4: Solving overlay */}
            {solving && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0D1014]/80 backdrop-blur-md">
                    <style>{`
                        @keyframes boltPulse {
                            0%, 100% { transform: scale(1); opacity: 1; }
                            50% { transform: scale(1.15); opacity: 0.8; }
                        }
                        @keyframes msgFadeIn {
                            from { opacity: 0; transform: translateY(6px); }
                            to { opacity: 1; transform: translateY(0); }
                        }
                        @keyframes progressFill {
                            from { width: 0%; }
                            to { width: 85%; }
                        }
                    `}</style>
                    <div className="bg-[#0D1014]/95 backdrop-blur-xl rounded-3xl border border-amber-500/20 shadow-[0_0_80px_rgba(245,158,11,0.15)] p-10 flex flex-col items-center gap-6 max-w-sm w-full mx-4">
                        <div className="w-20 h-20 rounded-3xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center"
                            style={{ animation: 'boltPulse 1.5s ease-in-out infinite' }}>
                            <svg className="w-10 h-10 text-amber-400" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                        </div>
                        <div className="text-center space-y-2">
                            <div className="text-lg font-black text-white">Generating Schedule</div>
                            <div key={solvingMessage} className="text-sm text-amber-400/70 h-5"
                                style={{ animation: 'msgFadeIn 0.4s ease-out' }}>
                                {solvingMessage}
                            </div>
                        </div>
                        <div className="w-full h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                            <div className="h-full rounded-full bg-gradient-to-r from-amber-500 to-orange-400"
                                style={{ animation: 'progressFill 30s cubic-bezier(0.1,0,0.4,1) forwards' }} />
                        </div>
                    </div>
                </div>
            )}
            <div className={`max-w-3xl mx-auto space-y-5 animate-in fade-in duration-300 pb-12 transition-all duration-500 ${solving ? 'opacity-20 pointer-events-none blur-sm' : ''}`}>
                <style>{`
                    @keyframes shimmerBtn {
                        0%, 100% { background-position: 0% 50%; }
                        50% { background-position: 100% 50%; }
                    }
                    @keyframes cardSlideUp {
                        from { opacity: 0; transform: translateY(12px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                `}</style>
                {/* Header */}
                <div className="flex items-center gap-4 pt-2 pb-2">
                    <button onClick={onCancel}
                        className="p-2.5 rounded-xl border border-white/8 bg-white/3 hover:bg-white/8 hover:border-white/15 transition-all shrink-0">
                        <svg className="w-5 h-5 text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-amber-500/15 border border-amber-500/30 shadow-[0_0_20px_rgba(245,158,11,0.15)] flex items-center justify-center shrink-0">
                            <svg className="w-6 h-6 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                        </div>
                        <div>
                            <h1 className="text-3xl font-black text-white tracking-tight leading-none">Auto Raid Creator</h1>
                            <p className="text-sm text-white/35 mt-1">Find optimal raid rosters from your guild's weekly availability</p>
                        </div>
                    </div>
                </div>

                {/* Configuration progress */}
                {(() => {
                    const steps = [
                        !!selectedGuildId,
                        selectedInstanceIds.length > 0,
                        true,
                        true,
                        true,
                        true,
                    ];
                    const completed = steps.filter(Boolean).length;
                    const pct = (completed / steps.length) * 100;
                    return (
                        <div className="flex items-center gap-3">
                            <div className="flex-1 h-1 rounded-full bg-white/[0.04] overflow-hidden">
                                <div className="h-full rounded-full bg-gradient-to-r from-amber-500/60 to-amber-400/40 transition-all duration-500" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-[10px] text-white/20 font-mono shrink-0">{completed}/{steps.length}</span>
                        </div>
                    );
                })()}

                {/* Guild */}
                <div style={{ animation: 'cardSlideUp 0.4s ease-out 0ms both' }}>
                <Section label="Guild" stepNumber={1}>
                    <select
                        value={selectedGuildId}
                        onChange={e => { setSelectedGuildId(e.target.value); setSelectedInstanceIds([]); setSelectedDays([]); }}
                        className="w-full rounded-xl border border-[#252830] bg-black/50 px-4 py-3 text-base text-white focus:border-amber-500/60 focus:outline-none transition-colors"
                    >
                        <option value="">Select a guild...</option>
                        {guilds.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                </Section>
                </div>

                {/* Instances */}
                {selectedGuildId && (
                    <div style={{ animation: 'cardSlideUp 0.4s ease-out 80ms both' }}>
                    <Section label="Raid Instances" hint="Solver creates as many raids as your roster supports" stepNumber={2}>
                        <div className="grid grid-cols-2 gap-3">
                            {raidInstances.map(inst => {
                                const active = selectedInstanceIds.includes(inst.id);
                                return (
                                    <button key={inst.id} onClick={() => toggleInstance(inst.id)}
                                        className={`relative p-5 rounded-xl border text-left transition-all group ${
                                            active
                                                ? "border-amber-500/60 bg-gradient-to-br from-amber-500/20 to-orange-600/5 shadow-[0_0_24px_rgba(245,158,11,0.18)]"
                                                : "border-[#252830] bg-[#0A0C10] hover:border-amber-500/25 hover:bg-[#0F1117] hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/20"
                                        }`}>
                                        {active && (
                                            <div className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center shadow-[0_0_12px_rgba(245,158,11,0.5)]">
                                                <svg className="w-3 h-3 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                        )}
                                        {/* FUN 1: cinematic instance image */}
                                        {inst.image_url && (
                                            <div className="absolute inset-0 overflow-hidden rounded-xl pointer-events-none">
                                                <img src={inst.image_url} alt="" className={`absolute right-0 top-0 h-full w-2/3 object-cover transition-all duration-500 ${active ? "opacity-45 group-hover:opacity-55" : "opacity-30 group-hover:opacity-40"} group-hover:scale-105`}
                                                    style={{ maskImage: 'linear-gradient(to left, black 0%, transparent 100%)', WebkitMaskImage: 'linear-gradient(to left, black 0%, transparent 100%)' }} />
                                            </div>
                                        )}
                                        <div className={`text-base font-black relative ${active ? "text-amber-100" : "text-white/75"}`}>{inst.label}</div>
                                        {inst.metadata?.size && (
                                            <div className={`flex items-center gap-2 text-xs mt-2 font-medium ${active ? "text-amber-400/70" : "text-white/30"}`}>
                                                <span className="inline-flex items-center gap-1">
                                                    <svg className="w-3 h-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    </svg>
                                                    {inst.metadata.size}
                                                </span>
                                                <span className="text-white/15">·</span>
                                                <span className="inline-flex items-center gap-1">
                                                    <svg className="w-3 h-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    {inst.metadata.duration || 60}m
                                                </span>
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                        {selectedInstanceIds.length > 1 && (() => {
                            const selectedSizes = selectedInstanceIds.map(id => raidInstances.find(i => i.id === id)).filter(Boolean).map(i => i!.metadata?.size || 0);
                            const allSameSize = new Set(selectedSizes).size <= 1;
                            const sizeDisplay = Array.from(new Set(selectedSizes)).join(", ");
                            return (
                                <div className="pt-1 space-y-1">
                                    <label className={`flex items-center gap-2.5 text-sm ${allSameSize ? 'text-white/60 cursor-pointer' : 'text-white/25 cursor-not-allowed'}`}>
                                        <input type="checkbox" checked={chained && allSameSize}
                                            onChange={e => { if (allSameSize) setChained(e.target.checked); }}
                                            disabled={!allSameSize}
                                            className="rounded border-white/20 bg-black/40 text-amber-500 focus:ring-amber-500 disabled:opacity-30" />
                                        Chain into one raid (combined lockout)
                                    </label>
                                    {!allSameSize && <p className="text-xs text-red-400/70 pl-6">Mismatched sizes ({sizeDisplay}-man) — can't chain.</p>}
                                </div>
                            );
                        })()}
                    </Section>
                    </div>
                )}

                {/* Reset Week */}
                {selectedGuildId && selectedInstanceIds.length > 0 && (
                    <div style={{ animation: 'cardSlideUp 0.4s ease-out 160ms both' }}>
                    <Section label="Reset Week" stepNumber={3}>
                        <div className="grid grid-cols-2 gap-3">
                            {[0, 1].map(offset => {
                                // Same anchor as the day picker: the guild's reset
                                // day, in UTC. Was a second copy of the Wed-only
                                // rule computed from browser-local time.
                                const startDt = resetInstantForWeek(
                                    getCurrentResetWeek(guildResetRule),
                                    { weekday: resetWeekdayIso, hour: 0, tz: 'UTC' }
                                ).plus({ weeks: offset }).startOf('day');
                                const wed = startDt.toJSDate();
                                const tue = startDt.plus({ days: 6 }).toJSDate();
                                const fmt = (d: Date) => formatMonthDay(d);
                                const active = weekOffset === offset;
                                return (
                                    <button key={offset} onClick={() => { setWeekOffset(offset); setSelectedDays([]); }}
                                        className={`py-4 px-5 rounded-xl border text-left transition-all ${
                                            active
                                                ? "border-amber-500/60 bg-gradient-to-br from-amber-500/20 to-orange-600/5 shadow-[0_0_20px_rgba(245,158,11,0.15)]"
                                                : "border-[#252830] bg-[#0A0C10] hover:border-amber-500/25"
                                        }`}>
                                        <div className="flex items-center gap-2">
                                            <div className={`text-base font-black ${active ? "text-amber-100" : "text-white/70"}`}>{offset === 0 ? "This Week" : "Next Week"}</div>
                                            {offset === 0 && <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/25">Current</span>}
                                        </div>
                                        <div className={`text-xs mt-1.5 font-mono ${active ? "text-amber-400/60" : "text-white/30"}`}>{fmt(wed)} – {fmt(tue)}</div>
                                    </button>
                                );
                            })}
                        </div>
                    </Section>
                    </div>
                )}

                {/* Schedule Window */}
                {selectedGuildId && selectedInstanceIds.length > 0 && (
                    <div style={{ animation: 'cardSlideUp 0.4s ease-out 240ms both' }}>
                    <Section
                        label="Schedule Window"
                        stepNumber={4}
                        trailing={
                            <label className="flex items-center gap-2 text-sm text-white/40 cursor-pointer select-none">
                                <input type="checkbox" checked={useTimeWindow} onChange={e => { setUseTimeWindow(e.target.checked); if (!e.target.checked) setSelectedDays([]); }}
                                    className="rounded border-white/20 bg-black/40 text-amber-500 focus:ring-amber-500" />
                                Constrain
                            </label>
                        }
                    >
                        {!useTimeWindow ? (
                            <p className="text-sm text-white/30 py-1">No constraint — solver scans the full Wed–Tue reset week.</p>
                        ) : (
                            <div className="space-y-4">
                                {/* Day picker */}
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-bold text-white/40 uppercase tracking-widest">Days</span>
                                        {selectedDays.length > 0 && (
                                            <button onClick={() => setSelectedDays([])} className="text-xs text-white/30 hover:text-white/60 transition-colors">Clear</button>
                                        )}
                                    </div>
                                    <div className="flex gap-1.5 flex-wrap">
                                        {wowWeekDays.map(day => {
                                            const active = selectedDays.includes(day.iso);
                                            return (
                                                <button key={day.iso} onClick={() => toggleDay(day.iso)}
                                                    className={`flex-1 min-w-[54px] flex flex-col items-center py-3.5 px-2 rounded-xl border text-center transition-all ${
                                                        active
                                                            ? "border-amber-500/60 bg-amber-500/20 shadow-[0_0_10px_rgba(245,158,11,0.2)]"
                                                            : "border-[#252830] bg-[#0A0C10] hover:border-amber-500/25 hover:bg-[#0F1117]"
                                                    }`}>
                                                    <span className={`text-sm font-black ${active ? "text-amber-200" : "text-white/60"}`}>{day.name}</span>
                                                    <span className={`text-[10px] mt-0.5 ${active ? "text-amber-400/70" : "text-white/25"}`}>{day.short}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <div className="flex items-center gap-2 mt-1">
                                        {selectedDays.length === 0 ? (
                                            <p className="text-xs text-white/25">No days selected — all days of the week are eligible.</p>
                                        ) : selectedDays.length < wowWeekDays.length ? (
                                            <button onClick={() => setSelectedDays(wowWeekDays.map(d => d.iso))}
                                                className="text-xs text-amber-400/50 hover:text-amber-400 font-bold transition-colors">
                                                Select All
                                            </button>
                                        ) : null}
                                    </div>
                                </div>
                                {/* Time range */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs font-bold text-white/40 uppercase tracking-widest block mb-2">No Earlier</label>
                                        <input type="time" value={noEarlier} onChange={e => setNoEarlier(e.target.value)}
                                            className="w-full rounded-xl border border-[#252830] bg-black/50 px-4 py-3 text-base text-white focus:border-amber-500/60 focus:outline-none transition-colors" />
                                        <p className="text-[10px] text-white/30 mt-1">UTC clock on each ARC day (Wed–Tue week), not your local TZ.</p>
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold text-white/40 uppercase tracking-widest block mb-2">No Later</label>
                                        <input type="time" value={noLater} onChange={e => setNoLater(e.target.value)}
                                            className="w-full rounded-xl border border-[#252830] bg-black/50 px-4 py-3 text-base text-white focus:border-amber-500/60 focus:outline-none transition-colors" />
                                        <p className="text-[10px] text-white/30 mt-1">Same UTC semantics as “No Earlier”.</p>
                                    </div>
                                </div>
                                {isOvernight && (
                                    <div className="text-sm text-amber-300 bg-amber-500/10 border border-amber-500/25 rounded-xl px-4 py-3">
                                        Overnight window: raids start from {noEarlier}, end by {noLater} the following day.
                                    </div>
                                )}
                            </div>
                        )}
                    </Section>
                    </div>
                )}

                {/* Duration Override */}
                {selectedGuildId && selectedInstanceIds.length > 0 && (
                    <div style={{ animation: 'cardSlideUp 0.4s ease-out 320ms both' }}>
                    <Section label="Duration Override" stepNumber={5}>
                        <select value={durationOverride || ""} onChange={e => setDurationOverride(e.target.value ? Number(e.target.value) : null)}
                            className="w-full rounded-xl border border-[#252830] bg-black/50 px-4 py-3 text-base text-white focus:border-amber-500/60 focus:outline-none transition-colors">
                            <option value="">Use instance default</option>
                            <option value="60">1 hour</option>
                            <option value="90">1.5 hours</option>
                            <option value="120">2 hours</option>
                            <option value="150">2.5 hours</option>
                            <option value="180">3 hours</option>
                            <option value="240">4 hours</option>
                        </select>
                    </Section>
                    </div>
                )}

                {/* Composition */}
                {selectedGuildId && selectedInstanceIds.length > 0 && (
                    <div style={{ animation: 'cardSlideUp 0.4s ease-out 400ms both' }}>
                    <Section label="Composition" stepNumber={6}>
                        {(() => {
                            const selectedSize = (() => {
                                const sizes = selectedInstanceIds.map(id => raidInstances.find(i => i.id === id)?.metadata?.size).filter(Boolean);
                                const seen = new Set<any>(); sizes.forEach(s => seen.add(s));
                                const unique = Array.from(seen);
                                return unique.length === 1 ? unique[0] : null;
                            })();
                            const visibleTemplates = selectedSize ? templates.filter(t => t.raid_size === selectedSize) : templates;
                            const hiddenCount = templates.length - visibleTemplates.length;
                            return (
                                <div className="space-y-2">
                                    <button onClick={() => setSelectedTemplateId(null)}
                                        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border text-left transition-all ${
                                            !selectedTemplateId ? "border-amber-500/50 bg-gradient-to-r from-amber-500/15 to-transparent" : "border-[#252830] bg-[#0A0C10] hover:border-amber-500/20"
                                        }`}>
                                        <span className={`text-sm font-bold ${!selectedTemplateId ? "text-amber-100" : "text-white/40"}`}>Use Instance Defaults</span>
                                        {!selectedTemplateId && <span className="text-xs text-amber-400 font-black tracking-widest">ACTIVE</span>}
                                    </button>
                                    {visibleTemplates.map(t => (
                                        <div key={t.id} className={`flex items-center gap-2 px-4 py-3 rounded-xl border transition-all ${
                                            selectedTemplateId === t.id ? "border-amber-500/50 bg-gradient-to-r from-amber-500/15 to-transparent" : "border-[#252830] bg-[#0A0C10] hover:border-amber-500/20"
                                        }`}>
                                            <button className="flex-1 text-left" onClick={() => setSelectedTemplateId(t.id)}>
                                                <div className="flex items-center gap-2">
                                                    <span className={`text-sm font-bold ${selectedTemplateId === t.id ? "text-amber-100" : "text-white/65"}`}>{t.name}</span>
                                                    {t.is_default && <span className="text-[10px] bg-amber-500/15 text-amber-400/70 px-1.5 py-0.5 rounded font-black">DEFAULT</span>}
                                                    {selectedTemplateId === t.id && <span className="text-xs text-amber-400 font-black tracking-widest ml-auto">ACTIVE</span>}
                                                </div>
                                                <div className="text-xs text-white/30 mt-0.5">{t.raid_size}-man · Tanks {t.tanks_min}–{t.tanks_max} · Healers {t.healers_min}–{t.healers_max}</div>
                                            </button>
                                            <button onClick={() => handleDeleteTemplate(t.id)}
                                                className="p-2 rounded-lg text-white/15 hover:text-red-400 hover:bg-red-500/10 transition-all">
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                    ))}
                                    {hiddenCount > 0 && <p className="text-xs text-white/25 text-center py-1">{hiddenCount} template{hiddenCount !== 1 ? "s" : ""} hidden (wrong raid size)</p>}
                                    {visibleTemplates.length === 0 && templates.length === 0 && <p className="text-xs text-white/25 text-center py-2">No templates yet.</p>}
                                </div>
                            );
                        })()}

                        {/* Spec bench preview */}
                        {selectedTemplateId && (() => {
                            const t = templates.find(t => t.id === selectedTemplateId);
                            const bench: (SpecDefinition | null)[] = t?.metadata?.spec_bench || [];
                            const filled = bench.filter(s => s !== null);
                            if (filled.length === 0) return null;
                            const groups: (SpecDefinition | null)[][] = [];
                            for (let i = 0; i < bench.length; i += 5) groups.push(bench.slice(i, i + 5));
                            return (
                                <div className="rounded-xl border border-[#1E2228] bg-black/20 p-4 space-y-3 mt-2">
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs font-black text-white/40 uppercase tracking-widest">Spec Bench</span>
                                        <span className="text-xs text-white/20">{filled.length}/{bench.length} seats</span>
                                    </div>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                                        {groups.map((group, gIdx) => (
                                            <div key={gIdx} className="rounded-lg border border-white/5 bg-black/20 p-2">
                                                <div className="text-[10px] text-white/20 font-black mb-1.5">G{gIdx + 1}</div>
                                                {group.map((seat, sIdx) => (
                                                    <div key={sIdx} className="flex items-center gap-1.5 py-0.5 text-xs">
                                                        {seat ? (
                                                            <>
                                                                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: getClassColor(seat.class_name) }} />
                                                                <span className="font-bold" style={{ color: getClassColor(seat.class_name) }}>{seat.spec}</span>
                                                            </>
                                                        ) : <span className="text-white/10">—</span>}
                                                    </div>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })()}

                        {/* ARC Hard Constraints */}
                        <div className="rounded-xl border border-amber-500/20 bg-gradient-to-br from-amber-500/8 to-transparent p-4 mt-2">
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-sm font-black text-amber-400/80 uppercase tracking-widest">ARC Hard Constraints</span>
                                <span className="text-xs text-white/20">applied on every solve</span>
                            </div>
                            {/* Minimum raiders floor — solver rejects partial raids below this size.
                                Defaults to null (strict: only complete raids). Bounded by the effective
                                raid size derived from the selected template or first selected instance. */}
                            {(() => {
                                const tmpl = templates.find(t => t.id === selectedTemplateId);
                                const instMeta = raidInstances.find(i => selectedInstanceIds.includes(i.id))?.metadata;
                                const effectiveSize: number = tmpl?.raid_size || instMeta?.size || 10;
                                const currentFloor = minRaidersFloor ?? effectiveSize;
                                const minBound = Math.max(1, constraintTanksMin + constraintHealersMin);
                                return (
                                    <div className="rounded-xl border border-amber-500/20 bg-black/20 p-3 mb-4">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-1.5 text-amber-300">
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                                </svg>
                                                <label className="text-xs font-black uppercase tracking-widest">Minimum Raiders</label>
                                            </div>
                                            <span className="text-[10px] text-white/30 font-mono">
                                                {minRaidersFloor === null ? `${effectiveSize} / ${effectiveSize}` : `${currentFloor} / ${effectiveSize}`}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <input type="range"
                                                min={minBound}
                                                max={effectiveSize}
                                                value={currentFloor}
                                                onChange={e => {
                                                    const v = Number(e.target.value);
                                                    setMinRaidersFloor(v >= effectiveSize ? null : v);
                                                }}
                                                className="flex-1 accent-amber-500"
                                            />
                                            <button
                                                onClick={() => setMinRaidersFloor(null)}
                                                className="text-[10px] font-bold text-white/30 hover:text-amber-300 px-2 py-1 rounded border border-white/10 hover:border-amber-500/30 transition-all"
                                                title="Reset to strict (full raids only)"
                                            >
                                                Strict
                                            </button>
                                        </div>
                                        <p className="text-[10px] text-white/30 mt-2">
                                            {minRaidersFloor === null
                                                ? `Only create complete ${effectiveSize}-player raids.`
                                                : `Accept raids with ${currentFloor}+ players (up to ${effectiveSize - currentFloor} PUG${effectiveSize - currentFloor !== 1 ? 's' : ''} needed).`}
                                        </p>
                                    </div>
                                );
                            })()}
                            <div className="grid grid-cols-2 gap-4">
                                {([
                                    { label: "Tanks", color: "text-sky-400", borderColor: "border-sky-500/20", icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>, min: constraintTanksMin, max: constraintTanksMax, setMin: setConstraintTanksMin, setMax: setConstraintTanksMax },
                                    { label: "Healers", color: "text-emerald-400", borderColor: "border-emerald-500/20", icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>, min: constraintHealersMin, max: constraintHealersMax, setMin: setConstraintHealersMin, setMax: setConstraintHealersMax },
                                ] as any[]).map(({ label, color, borderColor, icon, min, max, setMin, setMax }) => (
                                    <div key={label} className={`rounded-xl border ${borderColor} bg-black/20 p-3`}>
                                        <div className={`flex items-center gap-1.5 ${color} mb-3`}>
                                            {icon}
                                            <label className="text-xs font-black uppercase tracking-widest">{label}</label>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button onClick={() => setMin(Math.max(0, min - 1))} className="w-7 h-7 rounded-lg border border-[#252830] bg-black/40 hover:bg-white/5 text-white/50 hover:text-white font-black text-base flex items-center justify-center transition-all">−</button>
                                            <span className="w-7 text-center text-base font-black text-white">{min}</span>
                                            <button onClick={() => setMin(Math.min(max, min + 1))} className="w-7 h-7 rounded-lg border border-[#252830] bg-black/40 hover:bg-white/5 text-white/50 hover:text-white font-black text-base flex items-center justify-center transition-all">+</button>
                                            <span className="text-white/25 text-xs mx-1.5">to</span>
                                            <button onClick={() => setMax(Math.max(min, max - 1))} className="w-7 h-7 rounded-lg border border-[#252830] bg-black/40 hover:bg-white/5 text-white/50 hover:text-white font-black text-base flex items-center justify-center transition-all">−</button>
                                            <span className="w-7 text-center text-base font-black text-white">{max}</span>
                                            <button onClick={() => setMax(Math.min(20, max + 1))} className="w-7 h-7 rounded-lg border border-[#252830] bg-black/40 hover:bg-white/5 text-white/50 hover:text-white font-black text-base flex items-center justify-center transition-all">+</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* New template form */}
                        {!showNewTemplate ? (
                            <button onClick={() => setShowNewTemplate(true)} className="text-sm text-amber-400/60 hover:text-amber-300 font-bold transition-colors mt-1">
                                + New Template
                            </button>
                        ) : (
                            <div className="rounded-xl border border-[#252830] bg-black/20 p-5 space-y-4 mt-2">
                                <input value={newTemplateName} onChange={e => setNewTemplateName(e.target.value)}
                                    placeholder="Template name (e.g. Standard 10-man)"
                                    className="w-full rounded-xl border border-[#252830] bg-black/50 px-4 py-3 text-base text-white placeholder:text-white/20 focus:border-amber-500/60 focus:outline-none" />
                                <div className="grid grid-cols-5 gap-3">
                                    {[
                                        { label: "Size", el: <select value={newTemplateSize} onChange={e => setNewTemplateSize(Number(e.target.value))} className="w-full rounded-lg border border-[#252830] bg-black/50 px-2 py-2 text-sm text-white">{[5,10,25,40].map(s=><option key={s} value={s}>{s}</option>)}</select> },
                                        { label: "Tanks Min", el: <input type="number" min={0} max={10} value={newTemplateTanksMin} onChange={e=>setNewTemplateTanksMin(Number(e.target.value))} className="w-full rounded-lg border border-[#252830] bg-black/50 px-2 py-2 text-sm text-white" /> },
                                        { label: "Tanks Max", el: <input type="number" min={0} max={10} value={newTemplateTanksMax} onChange={e=>setNewTemplateTanksMax(Number(e.target.value))} className="w-full rounded-lg border border-[#252830] bg-black/50 px-2 py-2 text-sm text-white" /> },
                                        { label: "Heals Min", el: <input type="number" min={0} max={10} value={newTemplateHealersMin} onChange={e=>setNewTemplateHealersMin(Number(e.target.value))} className="w-full rounded-lg border border-[#252830] bg-black/50 px-2 py-2 text-sm text-white" /> },
                                        { label: "Heals Max", el: <input type="number" min={0} max={10} value={newTemplateHealersMax} onChange={e=>setNewTemplateHealersMax(Number(e.target.value))} className="w-full rounded-lg border border-[#252830] bg-black/50 px-2 py-2 text-sm text-white" /> },
                                    ].map(({ label, el }) => (
                                        <div key={label}><label className="text-xs text-white/40 block mb-1.5">{label}</label>{el}</div>
                                    ))}
                                </div>
                                {/* Spec Bench */}
                                <div className="border-t border-white/8 pt-4 space-y-3">
                                    <div>
                                        <label className="text-sm font-black text-white/50">Spec Bench <span className="text-xs font-normal text-white/25">(soft preferences)</span></label>
                                        <p className="text-xs text-white/25 mt-1">Drag specs from the palette onto seats. Right-click to clear a seat.</p>
                                    </div>
                                    <div className="space-y-1.5">
                                        <div className="text-[9px] font-black uppercase tracking-widest text-white/15 pt-1">Drag specs from here onto bench seats below</div>
                                        {Object.entries(WOW_SPECS).map(([className, specs]) => (
                                            <div key={className} className="flex items-center gap-2 flex-wrap">
                                                <span className="text-xs font-black w-16 shrink-0 text-right" style={{ color: getClassColor(className) }}>{className}</span>
                                                {specs.map(s => (
                                                    <button key={s.spec} type="button" draggable
                                                        onDragStart={() => setDraggedSpec({ spec: { class_name: className, spec: s.spec, role: s.role }, fromSeat: null })}
                                                        onDragEnd={() => setDraggedSpec(null)}
                                                        className="px-2.5 py-1 rounded-lg text-xs font-bold border border-[#252830] bg-black/30 hover:border-white/20 hover:bg-black/50 cursor-grab active:cursor-grabbing transition-all"
                                                        style={{ color: getClassColor(className) }}>
                                                        {s.spec}
                                                    </button>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                    {(() => {
                                        const benchSize = newTemplateSize;
                                        const currentBench = specBench.length === benchSize ? specBench : Array.from({ length: benchSize }, (_, i) => specBench[i] || null);
                                        if (currentBench.length !== specBench.length) setSpecBench(currentBench);
                                        const groups: (SpecDefinition | null)[][] = [];
                                        for (let i = 0; i < benchSize; i += 5) groups.push(currentBench.slice(i, i + 5));
                                        if (groups.length > 0) while (groups[groups.length-1].length < 5) groups[groups.length-1].push(null);
                                        const filledCount = currentBench.filter(s => s !== null).length;
                                        return (
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs font-black text-white/30 uppercase tracking-widest">Bench — {filledCount}/{benchSize} filled</span>
                                                    {filledCount > 0 && <button type="button" onClick={() => setSpecBench(Array.from({length:benchSize},()=>null))} className="text-xs text-red-400/50 hover:text-red-400 ml-auto">Clear All</button>}
                                                </div>
                                                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                                                    {groups.map((group, gIdx) => (
                                                        <div key={gIdx} className="rounded-xl border border-[#1E2228] bg-black/20 p-2">
                                                            <div className="mb-2 text-[10px] font-black uppercase tracking-widest text-white/20">Group {gIdx+1}</div>
                                                            <div className="space-y-1">
                                                                {group.map((seat, sIdx) => {
                                                                    const seatIdx = gIdx*5+sIdx;
                                                                    if (seatIdx >= benchSize) return null;
                                                                    return (
                                                                        <div key={seatIdx} draggable={!!seat}
                                                                            onDragStart={seat ? () => setDraggedSpec({ spec: seat, fromSeat: seatIdx }) : undefined}
                                                                            onDragEnd={() => setDraggedSpec(null)}
                                                                            onDragOver={e => e.preventDefault()}
                                                                            onDrop={() => { if (draggedSpec) { setSpecBench(prev => { const n=[...prev]; while(n.length<benchSize) n.push(null); if(draggedSpec.fromSeat!==null) n[draggedSpec.fromSeat]=null; n[seatIdx]={...draggedSpec.spec}; return n; }); setDraggedSpec(null); } }}
                                                                            onContextMenu={e => { e.preventDefault(); setSpecBench(prev => { const n=[...prev]; n[seatIdx]=null; return n; }); }}
                                                                            className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border transition-all min-h-[36px] ${seat ? "border-[#252830] bg-black/30 cursor-grab active:cursor-grabbing" : draggedSpec ? "border-dashed border-amber-500/40 bg-amber-500/5 hover:border-amber-500/60" : "border-dashed border-white/8 bg-black/10"}`}>
                                                                            {seat ? (
                                                                                <>
                                                                                    <div className="w-1 h-4 rounded-full shrink-0" style={{ backgroundColor: getClassColor(seat.class_name) }} />
                                                                                    <span className="text-xs font-bold" style={{ color: getClassColor(seat.class_name) }}>{seat.spec}</span>
                                                                                    <span className="text-[10px] text-white/25 ml-auto">{getRoleIcon(seat.role)}</span>
                                                                                </>
                                                                            ) : (
                                                                                <span className="flex items-center gap-1.5 text-xs text-white/15 italic">
                                                                                    {draggedSpec ? (
                                                                                        <>
                                                                                            <svg className="w-3 h-3 text-amber-400/60 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                                                                            </svg>
                                                                                            <span className="text-amber-400/60">Drop {draggedSpec.spec.spec}</span>
                                                                                        </>
                                                                                    ) : (
                                                                                        <>
                                                                                            <svg className="w-3 h-3 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                                                            </svg>
                                                                                            <span>Drag spec here</span>
                                                                                        </>
                                                                                    )}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                                <div className="flex gap-3 pt-1">
                                    <button onClick={handleCreateTemplate} disabled={!newTemplateName}
                                        className="px-5 py-2.5 bg-amber-600 hover:bg-amber-500 rounded-xl text-sm font-black text-white disabled:opacity-40 transition-all">
                                        Save Template
                                    </button>
                                    <button onClick={() => { setShowNewTemplate(false); setSpecBench([]); setDraggedSpec(null); }}
                                        className="px-5 py-2.5 border border-[#252830] bg-black/30 hover:bg-white/5 rounded-xl text-sm font-bold text-white/60 transition-all">
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}
                    </Section>
                    </div>
                )}

                {/* Error */}
                {solveError && (
                    <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-5 py-4 text-red-300 text-sm font-medium">
                        {solveError}
                    </div>
                )}

                {/* Generate Button */}
                {selectedGuildId && selectedInstanceIds.length > 0 && (
                    <button onClick={handleSolve} disabled={solving}
                        className="w-full py-6 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-black text-lg uppercase tracking-widest transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-[0_4px_40px_rgba(245,158,11,0.25)] hover:shadow-[0_4px_60px_rgba(245,158,11,0.45)] active:scale-[0.99] relative overflow-hidden"
                        style={!solving ? { backgroundSize: '200% 100%', animation: 'shimmerBtn 3s ease-in-out infinite' } : undefined}>
                        {solving ? (
                            <span className="flex items-center justify-center gap-3">
                                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                                <span className="flex flex-col items-center leading-tight">
                                    <span>Optimizing Roster...</span>
                                    <span className="text-xs font-normal opacity-60 normal-case tracking-normal">MILP solver is finding the best assignments</span>
                                </span>
                            </span>
                        ) : (
                            <span className="flex items-center justify-center gap-3">
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                                Generate Schedule
                            </span>
                        )}
                    </button>
                )}
            </div>
        </>);
    }

    // ============================================================================
    // RENDER: RESULTS STEP
    // ============================================================================
    if ((step === "results" || step === "committing") && results) {
        const groupedUnplaced: Record<string, UnplacedPlayer[]> = {};
        for (const u of results.unplaced) {
            if (!groupedUnplaced[u.reason]) groupedUnplaced[u.reason] = [];
            groupedUnplaced[u.reason].push(u);
        }

        return (<>
            <div className="w-full space-y-3 animate-in fade-in duration-300">
                <style>{`
                    @keyframes cardSlideUp {
                        from { opacity: 0; transform: translateY(12px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                `}</style>
                {/* Header */}
                <div className="space-y-3 pb-1">
                    <div className="flex items-center gap-3">
                        <button onClick={() => setStep("configure")} className="p-2 rounded-xl border border-white/8 bg-white/3 hover:bg-white/8 hover:border-white/15 transition-all shrink-0">
                            <svg className="w-5 h-5 text-white/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <div>
                            <h2 className="text-3xl font-black text-white tracking-tight leading-none flex items-center gap-3">
                                <svg className="w-7 h-7 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                                </svg>
                                ARC Results
                            </h2>
                            <p className="text-sm text-white/35 mt-1">Drag to rearrange · Click checkboxes to select · Commit when ready</p>
                        </div>
                    </div>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="inline-flex items-center gap-1.5 text-xs font-black px-3 py-1.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 shadow-[0_0_12px_rgba(245,158,11,0.12)]">
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                                {results.raids.length} RAID{results.raids.length !== 1 ? "S" : ""}
                            </span>
                            <span className="inline-flex items-center gap-1.5 text-xs font-black px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300">
                                {results.stats.players_placed || 0} PLACED
                            </span>
                            {results.unplaced.length > 0 && (
                                <button onClick={() => document.getElementById('arc-unplaced')?.scrollIntoView({ behavior: 'smooth' })}
                                    className="inline-flex items-center gap-1.5 text-xs font-black px-3 py-1.5 rounded-full bg-red-500/15 border border-red-500/30 text-red-300 hover:bg-red-500/25 transition-all cursor-pointer">
                                    <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                                    {results.unplaced.length} UNPLACED ↓
                                </button>
                            )}
                            <span className="text-xs text-white/20 font-mono">{results.stats.solve_time_seconds || 0}s</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 flex-wrap">
                            <button onClick={() => setShowAddRaid(v => !v)}
                                className="px-4 py-2.5 border border-[#252830] bg-black/30 hover:bg-white/5 hover:border-amber-500/25 rounded-xl text-sm font-bold text-white/60 hover:text-white/80 transition-all">
                                + Empty Raid
                            </button>
                            <button onClick={() => { setStep("configure"); setResults(null); }}
                                className="px-4 py-2.5 border border-[#252830] bg-black/30 hover:bg-white/5 hover:border-[#353840] rounded-xl text-sm font-bold text-white/60 hover:text-white/80 transition-all">
                                Re-configure
                            </button>
                            <div className="w-px h-6 bg-white/10 mx-1 hidden sm:block" />
                            <button onClick={handleCommit} disabled={selectedRaids.size === 0 || step === "committing"}
                                className="px-8 py-3 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 rounded-xl text-sm font-black text-white disabled:opacity-50 shadow-lg hover:shadow-[0_4px_30px_rgba(16,185,129,0.4)] uppercase tracking-wide transition-all active:scale-[0.98]">
                                {step === "committing" ? (
                                    <span className="flex items-center gap-2">
                                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                                        Committing...
                                    </span>
                                ) : `Commit ${selectedRaids.size} Raid${selectedRaids.size !== 1 ? "s" : ""}`}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Add Empty Raid panel */}
                {showAddRaid && (
                    <div className="rounded-xl border border-[#1C2028] bg-[#0D1014] p-4 flex flex-wrap items-end gap-3">
                        <div className="flex flex-col gap-1">
                            <label className="text-[10px] text-white/40 block mb-1">Date</label>
                            <input type="date" value={newRaidDate}
                                onChange={e => setNewRaidDate(e.target.value)}
                                placeholder={results.raids[0]?.date_iso || ""}
                                className="rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none" />
                            <PickerTzLabel />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-[10px] text-white/40 block mb-1">Start Time</label>
                            <input type="time" value={newRaidTime}
                                onChange={e => setNewRaidTime(e.target.value)}
                                className="rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none" />
                        </div>
                        <div className="text-[10px] text-white/30 self-center pb-1">
                            Using: <span className="text-white/50 font-bold">{raidInstances.find(i => selectedInstanceIds.includes(i.id))?.label || results.raids[0]?.instance_name || "same instance"}</span>
                        </div>
                        <div className="flex gap-2 ml-auto">
                            <button onClick={addEmptyRaid}
                                className="px-4 py-2 bg-amber-600 hover:bg-amber-500 rounded-lg text-xs font-bold text-white transition-all">
                                Create
                            </button>
                            <button onClick={() => setShowAddRaid(false)}
                                className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-xs font-bold text-white/60">
                                Cancel
                            </button>
                        </div>
                    </div>
                )}

                {commitError && (
                    <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-red-300 text-sm">{commitError}</div>
                )}

                {results.error && !results.raids.length && (
                    <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-6 text-center">
                        <p className="text-amber-200 font-bold text-lg mb-2">No Raids Could Be Formed</p>
                        <p className="text-amber-200/60 text-sm">{results.error}</p>
                    </div>
                )}

                {/* Raid Cards */}
                <div className={`${
                    results.raids.length === 1 ? "grid grid-cols-1 gap-4 items-start" :
                    results.raids.length === 2 ? "grid grid-cols-2 gap-4 items-start" :
                    "flex gap-4 overflow-x-auto pb-4 items-start scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent"
                }`} ref={containerRef}>
                    {results.raids.map((raid, raidIndex) => {
                        const isSelected = selectedRaids.has(raid.temp_id);
                        const isDropHover = dropTarget === raid.temp_id;
                        const isDropBad = isDropHover && dropInvalid;
                        const tanksOk = raid.role_breakdown.tanks >= 1;
                        const healersOk = raid.role_breakdown.healers >= 1;
                        return (
                            <div key={raid.temp_id}
                                ref={(el: HTMLDivElement | null) => { if (el) raidCardRefs.current.set(raid.temp_id, el); else raidCardRefs.current.delete(raid.temp_id); }}
                                style={{ animation: 'cardSlideUp 0.35s ease-out', animationDelay: `${raidIndex * 80}ms`, animationFillMode: 'backwards', ...(results.raids.length >= 3 ? { minWidth: '420px', flex: '1 0 420px' } : {}) }}
                                className={`rounded-xl border overflow-hidden transition-all duration-200 ${
                                    isDropBad ? "border-red-500/50 bg-red-500/8 ring-1 ring-red-500/30" :
                                    isDropHover ? "border-sky-400/50 bg-sky-500/8 ring-1 ring-sky-400/30" :
                                    isSelected ? "border-[#1C2028] bg-[#0D1014]" :
                                    "border-[#1C2028] bg-[#0D1014] opacity-70"
                                }`}>
                                {/* Raid Header */}
                                <div className={`relative px-4 py-3 border-b border-[#1C2028] overflow-hidden ${isSelected ? "bg-gradient-to-r from-white/[0.05] to-transparent" : ""}`}>
                                    {/* FUN 1: cinematic raid instance image in header */}
                                    {(() => {
                                        const inst = raidInstances.find(i => raid.instance_ids?.includes(i.id));
                                        return inst?.image_url ? (
                                            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                                                <img src={inst.image_url} alt="" className="absolute right-0 top-0 h-full w-1/2 object-cover opacity-30"
                                                    style={{ maskImage: 'linear-gradient(to left, black 0%, transparent 100%)', WebkitMaskImage: 'linear-gradient(to left, black 0%, transparent 100%)' }} />
                                            </div>
                                        ) : null;
                                    })()}
                                    <div className="relative flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2.5 min-w-0">
                                            <label className="shrink-0 flex items-center cursor-pointer">
                                                <input type="checkbox" checked={isSelected}
                                                    onChange={() => setSelectedRaids(prev => {
                                                        const next = new Set(prev);
                                                        next.has(raid.temp_id) ? next.delete(raid.temp_id) : next.add(raid.temp_id);
                                                        return next;
                                                    })}
                                                    className="w-4 h-4 rounded border-white/20 bg-black/40 text-emerald-500 focus:ring-emerald-500" />
                                            </label>
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[10px] font-black text-amber-400/50 font-mono">#{raidIndex + 1}</span>
                                                    <h3 className="text-base font-black text-white truncate">{raid.instance_name}</h3>
                                                </div>
                                                <div className="flex items-center gap-2 text-xs text-white/40 mt-0.5">
                                                    <span className="text-sky-400/80 font-mono font-bold">{raid.day} {raid.start_time}–{raid.end_time}</span>
                                                    <span className="text-white/20">·</span>
                                                    <span className="font-bold">{raid.size}<span className="text-white/25">/{raid.target_size}</span></span>
                                                    <div className="w-16 h-1.5 rounded-full bg-white/[0.06] overflow-hidden ml-1">
                                                        <div className={`h-full rounded-full transition-all duration-300 ${
                                                            raid.size >= raid.target_size ? "bg-emerald-400" :
                                                            raid.size >= raid.target_size * 0.7 ? "bg-amber-400" :
                                                            "bg-red-400/70"
                                                        }`} style={{ width: `${Math.min((raid.size / raid.target_size) * 100, 100)}%` }} />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {/* Role pills + delete */}
                                        <div className="flex items-center gap-1.5 shrink-0">
                                            <span className={`text-xs px-2 py-0.5 rounded-lg font-black ${tanksOk ? "bg-sky-500/15 text-sky-300 border border-sky-500/25" : "bg-red-500/20 text-red-300 border border-red-500/40 animate-pulse"}`}>
                                                {raid.role_breakdown.tanks}T
                                            </span>
                                            <span className={`text-xs px-2 py-0.5 rounded-lg font-black ${healersOk ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/25" : "bg-red-500/20 text-red-300 border border-red-500/40 animate-pulse"}`}>
                                                {raid.role_breakdown.healers}H
                                            </span>
                                            <span className="text-xs bg-orange-500/10 text-orange-300 px-2 py-0.5 rounded-lg font-black border border-orange-500/20">
                                                {raid.role_breakdown.dps}D
                                            </span>
                                            <button
                                                onClick={() => deleteRaid(raid.temp_id)}
                                                onMouseDown={e => e.stopPropagation()}
                                                className="p-1.5 rounded-lg text-white/15 hover:text-red-400 hover:bg-red-500/10 transition-all ml-0.5"
                                                title="Delete this raid (members return to unplaced)"
                                            >
                                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Members Grid — Groups of 5 with drag-and-drop */}
                                <div className="p-3.5">
                                <div className={`grid gap-2 ${results.raids.length === 1 ? "grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" : "grid-cols-2"}`}>
                                        {(() => {
                                            // members is a sparse array of length target_size; just chunk it directly
                                            const groups: (ArcMember | null)[][] = [];
                                            for (let i = 0; i < raid.members.length; i += 5) {
                                                groups.push(raid.members.slice(i, i + 5));
                                            }
                                            return groups.map((group, gIdx) => (
                                                <div key={gIdx} className="rounded-xl border border-[#1C2028] bg-black/20 overflow-hidden">
                                                    <div className="px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-white/25 bg-white/[0.02] border-b border-[#1C2028]">
                                                        Group {gIdx + 1}
                                                    </div>
                                                    <div className="p-1.5 space-y-1">
                                                        {group.map((m, sIdx) => {
                                                            const slotIndex = gIdx * 5 + sIdx;
                                                            const slotKey = `${raid.temp_id}:${slotIndex}`;
                                                            const isSlotTarget = dropSlotTarget?.raidId === raid.temp_id && dropSlotTarget?.slotIndex === slotIndex;
                                                            const registerSlot = (el: HTMLDivElement | null) => {
                                                                if (el) allSlotRefs.current.set(slotKey, el);
                                                                else allSlotRefs.current.delete(slotKey);
                                                            };

                                                            if (!m) {
                                                                // Empty seat — compressed to ~60% of occupied-card height.
                                                                // Still a full drop target (allSlotRefs keeps working) but visually
                                                                // recedes so partial raids stop drowning in repeated "EMPTY" rows.
                                                                // Expands to full height while a drag is in progress for easier targeting.
                                                                const expandedForDrag = !!dragSource;
                                                                return (
                                                                    <div key={`empty-${slotIndex}`}
                                                                        ref={registerSlot}
                                                                        className={`flex-none ${expandedForDrag ? 'h-[52px]' : 'h-[28px]'} w-full flex items-stretch gap-2 rounded-lg border px-2.5 py-1 transition-all select-none duration-100 border-dashed ${
                                                                            isSlotTarget && !dropInvalid
                                                                                ? "border-sky-400/60 bg-sky-500/10"
                                                                                : isSlotTarget && dropInvalid
                                                                                ? "border-red-500/50 bg-red-500/5"
                                                                                : "border-white/[0.06] bg-white/[0.01] hover:border-white/15"
                                                                        }`}>
                                                                        <div className="w-[3px] self-stretch rounded-full bg-white/5" />
                                                                        <span className="flex-1 text-[9px] text-white/20 uppercase tracking-widest font-medium flex items-center justify-center">
                                                                            {isSlotTarget && !dropInvalid ? `Slot ${slotIndex + 1}` : "Empty"}
                                                                        </span>
                                                                    </div>
                                                                );
                                                            }

                                                            // Occupied seat — RosterCard design
                                                            const isDraggedMember = dragSource?.type === "raid" && dragSource.memberId === m.character_id && dragSource.raidTempId === raid.temp_id;
                                                            const roleColor = getRoleColor(m.class_name, m.primary_spec, m.role, roleColors);
                                                            const displaySpec = m.primary_spec || getDisplaySpec(m.class_name, m.role);
                                                            const pickableSpecs = (WOW_SPECS[m.class_name] || []).filter(s =>
                                                                !m.spec_labels?.length || m.spec_labels.includes(s.spec)
                                                            );
                                                            const hasMultipleSpecs = pickableSpecs.length > 1;
                                                            const isSameUser = hoveredUserId === m.user_id && !isDraggedMember;
                                                            // Prefer a spec-specific icon (conveys class + role at once, e.g. Holy Paladin);
                                                            // fall back to class icon, then the coloured stripe. Mirrors PlannerPage's pattern.
                                                            const iconUrl = getSpecIconUrl(displaySpec, m.class_name, currentGameFields) || getClassIconUrl(m.class_name, currentGameFields);
                                                            const isSameSquad = !!(hoveredSquadId && m.squad_id === hoveredSquadId && !isDraggedMember);
                                                            return (
                                                                <div key={m.character_id}
                                                                    ref={registerSlot}
                                                                    className={`group/slot relative flex-none h-[56px] w-full flex items-center gap-2 rounded-lg border px-2 py-1.5 transition-all select-none duration-100 ${
                                                                        isDraggedMember
                                                                            ? "opacity-0 border-white/10 bg-black/60"
                                                                            : isSlotTarget && dropInvalid
                                                                            ? "border-red-500/60 bg-red-500/5 ring-1 ring-red-500/25"
                                                                            : isSlotTarget
                                                                            ? "border-sky-400/50 bg-sky-500/8 ring-1 ring-sky-400/25"
                                                                            : isSameUser
                                                                            ? "border-white/30 bg-white/10 cursor-grab active:cursor-grabbing"
                                                                            : "border-white/10 bg-black/60 cursor-grab hover:border-white/30 hover:bg-white/10 active:cursor-grabbing hover:shadow-lg"
                                                                    }`}
                                                                    style={!isSlotTarget && !isDraggedMember ? {
                                                                        borderColor: isSameUser || isSameSquad ? undefined : `${roleColor}50`,
                                                                        boxShadow: isSameUser
                                                                            ? `0 0 0 1px ${getClassColor(m.class_name)}50`
                                                                            : isSameSquad ? `0 0 0 1px rgba(129,140,248,0.6)` : undefined,
                                                                    } : undefined}
                                                                    onMouseEnter={() => setHoveredUserId(m.user_id)}
                                                                    onMouseLeave={() => setHoveredUserId(null)}
                                                                    onMouseDown={(e: React.MouseEvent) => {
                                                                        if (e.button !== 0) return;
                                                                        e.preventDefault();
                                                                        dragMovedRef.current = false;
                                                                        setIsDragging(false);
                                                                        setDragSource({ type: "raid", raidTempId: raid.temp_id, memberId: m.character_id, role: m.role });
                                                                        setDragPos({ x: e.clientX, y: e.clientY });
                                                                    }}
                                                                >
                                                                    {/* Role-colored left bar */}
                                                                    <div className="w-[3px] self-stretch rounded-full flex-shrink-0" style={{ backgroundColor: roleColor }} />
                                                                    {/* Class/spec icon — role colour reinforced via border */}
                                                                    {iconUrl && (
                                                                        <img src={iconUrl} alt=""
                                                                            className="h-10 w-10 flex-shrink-0 rounded object-cover bg-[#1a1a2e]"
                                                                            style={{ border: `1px solid ${roleColor}55` }}
                                                                            title={`${m.class_name} · ${displaySpec}`}
                                                                            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                                                                    )}
                                                                    {/* Two-line content: name on top, meta below */}
                                                                    <div className="flex-1 min-w-0 flex flex-col justify-center gap-0.5">
                                                                        {/* Line 1 — name + WCL parse pill (hidden when no parse data) */}
                                                                        <div className="flex items-center gap-1.5 min-w-0">
                                                                            <span className="truncate font-bold text-[13px] leading-none" style={{ color: getClassColor(m.class_name) }}>
                                                                                {m.character_name}
                                                                            </span>
                                                                            {m.wcl_perf > 0 && (
                                                                                <span
                                                                                    className="font-mono font-black text-[10px] leading-none tabular-nums shrink-0"
                                                                                    style={{ color: getParseColor(m.wcl_perf) }}
                                                                                    title={`Warcraft Logs parse: ${m.wcl_perf}`}
                                                                                >
                                                                                    {m.wcl_perf}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        {/* Line 2 — spec · squad · alt-raid indicator, all inline, small, scannable */}
                                                                        <div className="flex items-center gap-1 text-[10px] leading-none min-w-0">
                                                                            <span className="font-black uppercase tracking-wide shrink-0" style={{ color: roleColor }}>
                                                                                {displaySpec}
                                                                            </span>
                                                                            {m.squad_name && (() => {
                                                                                const sw = raid.squad_warnings.find(w => w.squad_id === m.squad_id);
                                                                                const isSplit = !!sw && (
                                                                                    sw.placed_elsewhere.some(pe => pe.names.length > 0) ||
                                                                                    sw.not_placed.length > 0
                                                                                );
                                                                                const tipText = isSplit ? `${m.squad_name} — split across raids` : m.squad_name;
                                                                                return (
                                                                                    <>
                                                                                        <span className="text-white/20 shrink-0">·</span>
                                                                                        <span
                                                                                            onMouseEnter={(e) => { e.stopPropagation(); setHoveredSquadId(m.squad_id); }}
                                                                                            onMouseLeave={() => setHoveredSquadId(null)}
                                                                                            className={`relative group/squad truncate cursor-help min-w-0 ${isSplit ? 'text-amber-400 font-bold' : 'text-indigo-400/80'}`}
                                                                                        >
                                                                                            {isSplit && '⚠ '}{m.squad_name}
                                                                                            {/* Themed tooltip — same pattern as RosterPage/GuildsPage */}
                                                                                            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-[#0a0a0f] border border-white/20 rounded-lg text-xs text-white/90 whitespace-nowrap opacity-0 pointer-events-none group-hover/squad:opacity-100 transition-opacity z-[999] shadow-2xl font-normal normal-case tracking-normal">
                                                                                                {tipText}
                                                                                            </span>
                                                                                        </span>
                                                                                    </>
                                                                                );
                                                                            })()}
                                                                            {/* Alt-raid availability — pushed to far right of the meta line */}
                                                                            {(() => {
                                                                                const altRaidIndices = m.also_available_raid_indices || [];
                                                                                const otherRaidCount = results.raids.length - 1;
                                                                                const isFullyFlexible = otherRaidCount > 0 && altRaidIndices.length >= otherRaidCount;
                                                                                const isLocked = altRaidIndices.length === 0;
                                                                                if (isFullyFlexible) {
                                                                                    return <span className="ml-auto shrink-0 text-emerald-400/90 font-black" title="Available for any raid">★</span>;
                                                                                }
                                                                                if (isLocked) {
                                                                                    if (results.raids.length <= 1) return null;
                                                                                    return <span className="ml-auto shrink-0 text-orange-400/60 font-black" title="Only available for this raid">only</span>;
                                                                                }
                                                                                if (altRaidIndices.length > 2) {
                                                                                    return <span className="ml-auto shrink-0 text-sky-300/90 font-black" title={altRaidIndices.map((i: number) => `#${i + 1}`).join(", ")}>
                                                                                        +{altRaidIndices.length}
                                                                                    </span>;
                                                                                }
                                                                                return <span className="ml-auto shrink-0 flex items-center gap-1">
                                                                                    {altRaidIndices.map((idx: number) => (
                                                                                        <span key={idx} className="text-sky-300/90 font-black" title={`Also fits Raid #${idx + 1}`}>
                                                                                            #{idx + 1}
                                                                                        </span>
                                                                                    ))}
                                                                                </span>;
                                                                            })()}
                                                                        </div>
                                                                    </div>
                                                                    {/* Multi-spec: small edit icon, always visible (replaces the clickable spec pill) */}
                                                                    {hasMultipleSpecs && (
                                                                        <button
                                                                            className="shrink-0 h-6 w-6 flex items-center justify-center rounded text-white/30 hover:bg-white/10 hover:text-white transition-colors"
                                                                            title="Change spec / role"
                                                                            onClick={(e) => { e.stopPropagation(); setSpecModalTarget({ memberId: m.character_id, raidTempId: raid.temp_id, member: m }); }}
                                                                            onMouseDown={(e) => e.stopPropagation()}
                                                                        >
                                                                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536L16.732 3.732z" />
                                                                            </svg>
                                                                        </button>
                                                                    )}
                                                                    {/* Remove button — floating red circle at top-right corner, hover reveal (mirrors PlannerPage pattern) */}
                                                                    <button
                                                                        type="button"
                                                                        className="absolute -top-2 -right-2 h-5 w-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover/slot:opacity-100 transition-all shadow-lg z-20 hover:scale-110"
                                                                        title="Remove from raid"
                                                                        onClick={(e) => { e.stopPropagation(); removePlayerFromRaid(m.character_id, raid.temp_id); }}
                                                                        onMouseDown={(e) => e.stopPropagation()}
                                                                    >
                                                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                                                                    </button>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {/* Drag ghost */}
                    {dragSource && isDragging && dragPos && containerRef.current && (() => {
                        let charName = "";
                        let charClass = "";
                        let role = "";
                        let ghostPrimarySpec = "";
                        if (dragSource.type === "raid") {
                            const raid = results.raids.find(r => r.temp_id === dragSource.raidTempId);
                            const member = raid?.members.find(m => m && m.character_id === dragSource.memberId);
                            if (member) { charName = member.character_name; charClass = member.class_name; role = member.role; ghostPrimarySpec = member.primary_spec; }
                        } else if (dragSource.type === "unplaced") {
                            const player = results.unplaced.find(u => u.character_id === dragSource.charId);
                            if (player) { charName = player.character_name; charClass = player.class_name; role = player.roles[0] || "DPS"; ghostPrimarySpec = player.primary_spec; }
                        }
                        const ghostSpec = ghostPrimarySpec || getDisplaySpec(charClass, role);
                        const ghostRoleColor = getRoleColor(charClass, ghostPrimarySpec, role, roleColors);
                        return (
                            <div className="pointer-events-none fixed z-[9999]" style={{ left: dragPos.x, top: dragPos.y, transform: "translate(-50%, -50%)", width: 220 }}>
                                <div className={`rotate-2 scale-105 drop-shadow-xl flex-none h-[52px] w-full flex items-stretch gap-2 rounded-lg border px-2.5 py-2 select-none ${
                                    dropInvalid ? "bg-[#1a0a0a]/95 border-red-500/60" : "bg-[#0D1014]/95 border-white/20"
                                }`} style={!dropInvalid ? { borderColor: `${ghostRoleColor}50` } : undefined}>
                                    <div className="w-[3px] self-stretch rounded-full flex-shrink-0" style={{ backgroundColor: ghostRoleColor }} />
                                    <div className="flex-1 min-w-0 flex flex-col justify-center">
                                        <span className="truncate font-bold text-sm leading-none" style={{ color: getClassColor(charClass) }}>{charName}</span>
                                        <span className="text-[10px] text-white/50 leading-none mt-0.5">{charClass}</span>
                                    </div>
                                    <span className="flex-shrink-0 self-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase"
                                        style={{ backgroundColor: `${ghostRoleColor}20`, color: ghostRoleColor }}>
                                        {ghostSpec}
                                    </span>
                                    {dropInvalid && <span className="flex-shrink-0 self-center text-[9px] text-red-400 font-bold">✗</span>}
                                </div>
                            </div>
                        );
                    })()}
                </div>

                {/* Unplaced Players */}
                {results.unplaced.length > 0 && (
                    <div id="arc-unplaced" className="rounded-2xl border border-[#1C2028] bg-[#0D1014] overflow-hidden">
                        <div className="px-5 py-3 border-b border-[#1C2028] bg-gradient-to-r from-red-500/[0.04] to-transparent flex items-center gap-3 flex-wrap">
                            <span className="text-xs font-black uppercase tracking-widest text-white/50 flex items-center gap-2">
                                Unplaced
                                <span className="inline-flex items-center gap-1 bg-red-500/20 border border-red-500/30 text-red-400 px-2.5 py-0.5 rounded-full text-sm font-black">
                                    <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                                    {results.unplaced.length}
                                </span>
                            </span>
                            <div className="flex items-center gap-3 text-[10px] text-white/25 ml-auto flex-wrap">
                                {Object.entries(groupedUnplaced).map(([reason, players]) => {
                                    const { label, color } = getReasonLabel(reason);
                                    return <span key={reason}><span className={`${color} font-bold`}>{players.length}</span> {label}</span>;
                                })}
                            </div>
                        </div>
                        <div className="divide-y divide-[#1C2028]">
                        {Object.entries(groupedUnplaced).sort(([a], [b]) => {
                            // role_full is the most actionable bucket — players ready & willing,
                            // just edged out by better matches. Surface them first so RLs can
                            // manually swap them in without hunting.
                            if (a === 'role_full') return -1;
                            if (b === 'role_full') return 1;
                            return 0;
                        }).map(([reason, bucketPlayers]) => {
                            const { label: reasonLabel, color: reasonColor } = getReasonLabel(reason);
                            return (
                                <div key={reason} className="p-3">
                                    <div className="flex items-center gap-2 mb-2.5">
                                        <span className={`text-[10px] font-black uppercase tracking-widest ${reasonColor}`}>{reasonLabel}</span>
                                        <span className="text-[10px] text-white/25 font-mono">{bucketPlayers.length}</span>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                                    {bucketPlayers.map(p => {
                                const pSpec = p.primary_spec || (p.roles.length > 0 ? getDisplaySpec(p.class_name, p.roles[0]) : "—");
                                const pRoleColor = getRoleColor(p.class_name, p.primary_spec, p.roles[0] || "DPS", roleColors);
                                // Icon: spec-specific if we can resolve one, else class icon. Consistent with roster cards & PlannerPage.
                                const pIconUrl = getSpecIconUrl(pSpec, p.class_name, currentGameFields) || getClassIconUrl(p.class_name, currentGameFields);
                                const pIsSameSquad = !!(hoveredSquadId && p.squad_id === hoveredSquadId);
                                return (
                                    <div key={p.character_id}
                                        className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border select-none transition-all ${
                                            p.available_raids.length > 0
                                                ? hoveredUserId === p.user_id || pIsSameSquad
                                                    ? "border-white/20 bg-black/40 cursor-grab active:cursor-grabbing"
                                                    : "border-[#1C2028] bg-black/20 cursor-grab active:cursor-grabbing hover:border-[#2a2d35] hover:bg-black/30"
                                                : "border-[#161820] bg-black/10 opacity-40"
                                        }`}
                                        style={
                                            hoveredUserId === p.user_id && p.available_raids.length > 0
                                                ? { boxShadow: `0 0 0 1px ${getClassColor(p.class_name)}50` }
                                                : pIsSameSquad
                                                ? { boxShadow: `0 0 0 1px rgba(129,140,248,0.6)` }
                                                : undefined
                                        }
                                        onMouseEnter={() => setHoveredUserId(p.user_id)}
                                        onMouseLeave={() => setHoveredUserId(null)}
                                        onMouseDown={(e: React.MouseEvent) => {
                                            if (e.button !== 0 || p.available_raids.length === 0) return;
                                            e.preventDefault();
                                            setIsDragging(false);
                                            setDragSource({ type: "unplaced", charId: p.character_id, roles: p.roles });
                                            setDragPos({ x: e.clientX, y: e.clientY });
                                        }}
                                    >
                                        {pIconUrl ? (
                                            <img src={pIconUrl} alt=""
                                                className="h-7 w-7 flex-shrink-0 rounded border border-white/10 object-cover bg-[#1a1a2e]"
                                                title={`${p.class_name} · ${pSpec}`}
                                                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                                        ) : (
                                            <div className="w-1 h-7 rounded-full shrink-0" style={{ backgroundColor: getClassColor(p.class_name) }} />
                                        )}
                                        <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1 min-w-0">
                                                <span className="text-sm font-bold truncate leading-none" style={{ color: getClassColor(p.class_name) }}>{p.character_name}</span>
                                                {p.wcl_perf > 0 && (
                                                    <span
                                                        className="font-mono font-black text-[10px] leading-none tabular-nums shrink-0"
                                                        style={{ color: getParseColor(p.wcl_perf) }}
                                                        title={`Warcraft Logs parse: ${p.wcl_perf}`}
                                                    >
                                                        {p.wcl_perf}
                                                    </span>
                                                )}
                                                {p.squad_name && (
                                                    <span
                                                        onMouseEnter={(e) => { e.stopPropagation(); setHoveredSquadId(p.squad_id); }}
                                                        onMouseLeave={() => setHoveredSquadId(null)}
                                                        className="text-indigo-400/50 text-[10px] shrink-0 hover:text-indigo-300 cursor-help"
                                                    >
                                                        · {p.squad_name}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
                                                <span className="text-xs font-semibold shrink-0" style={{ color: pRoleColor }}>{pSpec}</span>
                                                {/* Spec icons for each alternate role the player can cover. Picks a
                                                    representative spec per role, preferring one from the player's own
                                                    spec_labels so the icon reflects what they actually play (e.g. Feral
                                                    Tank vs Feral DPS for a Druid). Single-role players skip this to
                                                    avoid redundancy with the spec label to their left. Icon border
                                                    echoes the role colour used everywhere else on the page. */}
                                                {p.roles.length > 1 && (
                                                    <span className="flex items-center gap-1 shrink-0">
                                                        {['Tank', 'Healer', 'DPS'].filter(r => p.roles.includes(r)).map(r => {
                                                            const specsForClass = WOW_SPECS[p.class_name] || [];
                                                            const candidates = specsForClass.filter(s => {
                                                                const matches = s.role === r || (r === 'DPS' && (s.role === 'Melee DPS' || s.role === 'Ranged DPS'));
                                                                return matches && (!p.spec_labels?.length || p.spec_labels.includes(s.spec));
                                                            });
                                                            const spec = candidates[0]?.spec;
                                                            if (!spec) return null;
                                                            const iconUrl = getSpecIconUrl(spec, p.class_name, currentGameFields);
                                                            if (!iconUrl) return null;
                                                            const col = r === 'Tank' ? (roleColors['Tank'] || '#38bdf8')
                                                                      : r === 'Healer' ? (roleColors['Healer'] || '#34d399')
                                                                      : (roleColors['Melee DPS'] || '#fb923c');
                                                            return (
                                                                <img key={r} src={iconUrl} alt=""
                                                                    className="w-4 h-4 rounded object-cover bg-[#1a1a2e] shrink-0"
                                                                    style={{ border: `1px solid ${col}60` }}
                                                                    title={`Can play ${spec} (${r})`}
                                                                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                                                            );
                                                        })}
                                                    </span>
                                                )}
                                                <span className={`text-[10px] ${getReasonLabel(p.reason).color} truncate`}>{getReasonLabel(p.reason).label}</span>
                                            </div>
                                        </div>
                                        {(() => {
                                            // BUG 1b: only show buttons for raids that still exist
                                            const validRaids = p.available_raids.filter(rid =>
                                                results.raids.some(r => r.temp_id === rid)
                                            );
                                            if (validRaids.length === 0) return null;
                                            return (
                                                <div className="flex items-center gap-1 shrink-0">
                                                    {validRaids.map(raidId => {
                                                        const raidForBtn = results.raids.find(r => r.temp_id === raidId);
                                                        const raidIdx = results.raids.findIndex(r => r.temp_id === raidId);
                                                        const isFull = raidForBtn ? !raidForBtn.members.some(m => m === null) : false;
                                                        const isBiloc = raidForBtn ? isUserBilocated(p.user_id, raidForBtn, results.raids) : false;
                                                        const isUnavailable = raidForBtn ? !isCharAvailableForRaid(p.availability, raidForBtn.date_iso, raidForBtn.start_time, raidForBtn.duration_minutes, userTz) : false;
                                                        const isDisabled = isFull || isBiloc || isUnavailable;
                                                        const tooltip = isFull ? `Raid #${raidIdx + 1} is full` : isBiloc ? `${p.character_name} is already in an overlapping raid` : isUnavailable ? `${p.character_name} is not available on ${raidForBtn?.day} at ${raidForBtn?.start_time}` : `Add to Raid #${raidIdx + 1}`;
                                                        return (
                                                            <button
                                                                key={raidId}
                                                                onClick={() => {
                                                                    if (isUnavailable && raidForBtn) {
                                                                        showToast(`${p.character_name} is not available on ${raidForBtn.day} at ${raidForBtn.start_time}`, 'error');
                                                                        return;
                                                                    }
                                                                    if (!isDisabled) addUnplacedToRaid(p.character_id, raidId, p.roles[0] || "DPS");
                                                                }}
                                                                onMouseDown={(e) => e.stopPropagation()}
                                                                disabled={isDisabled}
                                                                className={`w-7 h-7 flex items-center justify-center rounded-lg border text-xs font-black transition-all ${
                                                                    isDisabled
                                                                        ? "border-white/10 bg-white/5 text-white/20 cursor-not-allowed"
                                                                        : "border-sky-500/30 bg-sky-600/15 text-sky-300 hover:bg-sky-500 hover:text-black cursor-pointer"
                                                                }`}
                                                                title={tooltip}
                                                            >
                                                                {raidIdx + 1}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })()}
                                        </div>
                                    );
                                })}
                                        </div>
                                    </div>
                                );
                            })}
                            </div>
                        </div>
                    )}
                </div>

            {/* Spec picker modal — fixed overlay, rendered at fragment level so it escapes any overflow:hidden */}
            {specModalTarget && (() => {
                const { memberId, raidTempId, member: m } = specModalTarget;
                const allSpecs = WOW_SPECS[m.class_name] || [];
                // Map solver's "DPS" back to WOW_SPECS DPS roles for filtering
                const canPlayRole = (specRole: string) => {
                    if (specRole === "Tank") return (m.available_roles || []).includes("Tank");
                    if (specRole === "Healer") return (m.available_roles || []).includes("Healer");
                    return (m.available_roles || []).includes("DPS");
                };
                const roleGroups = [
                    { key: "Tank", label: "Tank", color: roleColors["Tank"] || "#38bdf8", icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" },
                    { key: "Healer", label: "Healer", color: roleColors["Healer"] || "#34d399", icon: "M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" },
                    { key: "Melee DPS", label: "Melee DPS", color: roleColors["Melee DPS"] || "#fb923c", icon: "M13 10V3L4 14h7v7l9-11h-7z" },
                    { key: "Ranged DPS", label: "Ranged DPS", color: roleColors["Ranged DPS"] || "#a78bfa", icon: "M13 10V3L4 14h7v7l9-11h-7z" },
                ];
                return (
                    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={() => setSpecModalTarget(null)}>
                        <div className="bg-[#0D1014]/98 backdrop-blur-xl rounded-2xl border border-[#252830] shadow-[0_0_60px_rgba(0,0,0,0.6)] p-5 w-72" onClick={e => e.stopPropagation()}>
                            {/* Header */}
                            <div className="flex items-center gap-3 mb-5">
                                <div className="w-2.5 h-7 rounded-full shrink-0" style={{ backgroundColor: getClassColor(m.class_name) }} />
                                <div>
                                    <div className="font-black text-white text-sm leading-tight">{m.character_name}</div>
                                    <div className="text-white/40 text-xs">{m.class_name}</div>
                                </div>
                                <button onClick={() => setSpecModalTarget(null)} className="ml-auto w-7 h-7 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 text-white/30 hover:text-white/60 transition-all text-lg leading-none">×</button>
                            </div>
                            {/* Role groups */}
                            <div className="space-y-4">
                                {roleGroups.map(({ key, label, color, icon }) => {
                                    const specs = allSpecs.filter(s => s.role === key);
                                    if (specs.length === 0) return null;
                                    const canPlay = canPlayRole(key);
                                    return (
                                        <div key={key}>
                                            <div className="flex items-center gap-2 mb-2">
                                                <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color }}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
                                                </svg>
                                                <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: `${color}99` }}>{label}</span>
                                            </div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {specs.map(s => {
                                                    const isActive = s.spec === m.primary_spec;
                                                    // Disabled if: role not available OR spec not in player's selected specs
                                                    const hasSpec = !m.spec_labels?.length || m.spec_labels.includes(s.spec);
                                                    const enabled = canPlay && hasSpec;
                                                    return (
                                                        <button key={s.spec}
                                                            onClick={() => { if (enabled) { changePlayerSpec(memberId, raidTempId, s.spec, s.role); setSpecModalTarget(null); } }}
                                                            disabled={!enabled}
                                                            title={!canPlay ? "Not in this player's role set" : !hasSpec ? "Player hasn't selected this spec" : undefined}
                                                            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                                                                isActive
                                                                    ? "border text-white font-black shadow-sm"
                                                                    : enabled
                                                                        ? "border border-white/10 bg-white/4 text-white/60 hover:border-white/25 hover:bg-white/8 hover:text-white/90"
                                                                        : "border border-white/5 bg-white/2 text-white/20 cursor-not-allowed"
                                                            }`}
                                                            style={isActive ? { borderColor: `${color}60`, backgroundColor: `${color}18`, color } : undefined}
                                                        >
                                                            {s.spec}
                                                            {s.role.includes("Melee") ? <span className="text-[9px] ml-1 opacity-50">M</span> : s.role.includes("Ranged") ? <span className="text-[9px] ml-1 opacity-50">R</span> : null}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            {!canPlay && <p className="text-[10px] text-white/25 mt-1">Not in this player's role set</p>}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                );
            })()}
        </>);
    }

    // ============================================================================
    // RENDER: DONE STEP
    // ============================================================================
    if (step === "done") {
        const funMsg = COMMIT_MESSAGES[Math.floor(Math.random() * COMMIT_MESSAGES.length)];
        return (
            <div className="max-w-sm mx-auto text-center py-20 animate-in fade-in duration-500 space-y-5">
                <style>{`
                    @keyframes celebPulse {
                        0%, 100% { box-shadow: 0 0 40px rgba(16,185,129,0.25); }
                        50% { box-shadow: 0 0 80px rgba(16,185,129,0.5), 0 0 120px rgba(16,185,129,0.2); }
                    }
                `}</style>
                <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-emerald-500/30 to-green-600/20 border border-emerald-500/40 flex items-center justify-center"
                    style={{ animation: 'celebPulse 2s ease-in-out infinite' }}>
                    <svg className="w-10 h-10 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <div>
                    <h2 className="text-3xl font-black text-white tracking-tight">Raids Committed!</h2>
                    {committedStats && (
                        <p className="text-emerald-400/80 font-black text-base mt-2">
                            {committedStats.raids} raid{committedStats.raids !== 1 ? 's' : ''} · {committedStats.players} player{committedStats.players !== 1 ? 's' : ''}
                        </p>
                    )}
                </div>
                <p className="text-white/30 text-sm italic">"{funMsg}"</p>
                <p className="text-white/35 text-sm">Your raids are live on the planner and visible to guild members.</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
                    <button onClick={onCommitSuccess}
                        className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 rounded-xl text-sm font-black text-white shadow-lg hover:shadow-[0_4px_30px_rgba(245,158,11,0.3)] transition-all">
                        View on Planner
                    </button>
                    <button onClick={() => { setStep("configure"); setResults(null); setCommittedStats(null); }}
                        className="px-6 py-3 border border-[#252830] bg-black/30 hover:bg-white/5 hover:border-white/15 rounded-xl text-sm font-bold text-white/60 hover:text-white/80 transition-all">
                        Schedule More Raids
                    </button>
                </div>
            </div>
        );
    }

    return null;
}
